import { useEffect, useState } from "react";
import { Routes, Route, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { api, fmtEUR, mediaUrl, API } from "@/lib/api";
import { formatLocDisplay, formatLocString } from "@/components/WarehouseExtras";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import WarehouseMap from "./WarehouseMap";
import { LayoutDashboard, Package, ShoppingBag, Camera, LogOut, Plus, Trash2, Edit3, Sliders, Upload, Settings as SettingsIcon, Grid3x3, ArrowUp, ArrowDown, Warehouse, ScanLine, MapPin, X, Phone as PhoneIcon, Check, ArrowLeft, User as UserIcon, Printer, Clock, Facebook, Instagram, Youtube, MessageCircle, Mail, AlertTriangle, Users, Ban, Save, Search as SearchIcon, FileText, Power, KeyRound, Store, BarChart3, Gift, QrCode } from "lucide-react";
import { LocationPicker } from "@/components/StockModals";

const ORDER_STATUSES = ["E re", "Në përpunim", "Duke u përgatitur", "Dërguar", "Përfunduar", "Anuluar"];
const REQ_STATUSES = ["E re", "Në kontroll", "Pjesa u gjet", "Ofertë dërguar", "Përfunduar"];

const AdminNav = () => {
  const loc = useLocation();
  const { logout } = useApp();
  const nav = useNavigate();
  const items = [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/admin/produktet", icon: Package, label: "Produktet" },
    { to: "/admin/alarm-stoku", icon: AlertTriangle, label: "Alarm stoku" },
    { to: "/skano", icon: ScanLine, label: "Skano Stokun" },
    { to: "/admin/dollapet", icon: Warehouse, label: "Dollapët" },
    { to: "/admin/kategorite", icon: Grid3x3, label: "Kategoritë" },
    { to: "/admin/konfigurator", icon: Sliders, label: "Konfigurator" },
    { to: "/admin/porosite", icon: ShoppingBag, label: "Porositë" },
    { to: "/admin/klientet", icon: Users, label: "Klientët" },
    { to: "/admin/bonuset", icon: Gift, label: "Bonuset" },
    { to: "/admin/punetoret", icon: KeyRound, label: "Punëtorët" },
    { to: "/admin/personeli", icon: Users, label: "Pushime & Paga" },
    { to: "/admin/pos-raport", icon: Store, label: "Raport POS" },
    { to: "/admin/kerkesat", icon: Camera, label: "Kërkesat me foto" },
    { to: "/admin/kontakt", icon: PhoneIcon, label: "Kontakt" },
    { to: "/admin/tekstet", icon: FileText, label: "Tekstet e Faqes" },
    { to: "/admin/cilesimet", icon: SettingsIcon, label: "Cilësimet" },
  ];
  return (
    <aside className="hg-card p-3 h-fit md:sticky md:top-20 flex md:flex-col gap-1 overflow-x-auto no-scrollbar print:hidden">
      {items.map(it => (
        <Link key={it.to} to={it.to} data-testid={`admin-nav-${it.label}`}
          className={`flex items-center gap-2 px-3 py-2.5 text-sm whitespace-nowrap ${loc.pathname === it.to ? "bg-[#FFD600] text-black font-bold" : "hover:bg-[#262626]"}`}>
          <it.icon size={16} /> {it.label}
        </Link>
      ))}
      <button onClick={() => { logout(); nav("/login"); }} data-testid="admin-logout"
        className="flex items-center gap-2 px-3 py-2.5 text-sm text-red-400 hover:bg-[#262626] mt-2 md:mt-4 whitespace-nowrap">
        <LogOut size={16} /> Dil
      </button>
    </aside>
  );
};

const SalesDashboardCards = () => {
  const [sales, setSales] = useState(null);
  const [openMonthly, setOpenMonthly] = useState(false);
  useEffect(() => { api.get("/admin/sales/summary").then(r => setSales(r.data)); }, []);
  if (!sales) return null;
  const t = sales.today;
  const totalToday = (t.pos_revenue || 0) + (t.online_revenue || 0);
  return (
    <>
      <div className="mb-3">
        <div className="text-xs uppercase text-neutral-400 tracking-widest mb-2 flex items-center justify-between">
          <span>Shitja Sot</span>
          <button onClick={() => setOpenMonthly(true)} data-testid="dash-sales-monthly"
            className="text-[10px] uppercase tracking-wider text-[#FFD600] hover:underline inline-flex items-center gap-1">
            <BarChart3 size={12} /> Përmbledhja mujore
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button onClick={() => setOpenMonthly(true)} data-testid="sales-today-butik"
            className="hg-card p-4 border-l-4 border-emerald-400 text-left hover:border-[#FFD600] transition-colors">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs uppercase text-neutral-400">Butik (Arka)</div>
              <Store size={14} className="text-emerald-400" />
            </div>
            <div className="font-display text-3xl text-emerald-400">{fmtEUR(t.pos_revenue)}</div>
            <div className="text-[10px] text-neutral-500 mt-1">{t.pos_count} fatura • TVSH: {fmtEUR(t.pos_vat)}</div>
          </button>
          <button onClick={() => setOpenMonthly(true)} data-testid="sales-today-online"
            className="hg-card p-4 border-l-4 border-cyan-400 text-left hover:border-[#FFD600] transition-colors">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs uppercase text-neutral-400">Online</div>
              <ShoppingBag size={14} className="text-cyan-400" />
            </div>
            <div className="font-display text-3xl text-cyan-400">{fmtEUR(t.online_revenue)}</div>
            <div className="text-[10px] text-neutral-500 mt-1">{t.online_count} porosi • TVSH: {fmtEUR(t.online_vat)}</div>
          </button>
          <div className="hg-card p-4 border-l-4 border-[#FFD600]" data-testid="sales-today-total">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs uppercase text-neutral-400">Totali sot</div>
              <BarChart3 size={14} className="text-[#FFD600]" />
            </div>
            <div className="font-display text-3xl text-[#FFD600]">{fmtEUR(totalToday)}</div>
            <div className="text-[10px] text-neutral-500 mt-1">TVSH totale: {fmtEUR((t.pos_vat || 0) + (t.online_vat || 0))}</div>
          </div>
        </div>
      </div>

      {openMonthly && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 overflow-y-auto" onClick={() => setOpenMonthly(false)}>
          <div className="hg-card w-full max-w-4xl my-4" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-[#1F2B44] flex items-center justify-between sticky top-0 bg-[#131E30] z-10">
              <div>
                <div className="text-xs uppercase text-neutral-400">Përmbledhje 12 muajshe</div>
                <div className="font-display text-xl">Shitja për muaj</div>
              </div>
              <button onClick={() => setOpenMonthly(false)} data-testid="sales-modal-close" className="p-2 hover:bg-[#0B1220]"><X size={18} /></button>
            </div>
            <div className="p-3 max-h-[75vh] overflow-y-auto">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="text-[10px] uppercase text-neutral-400 border-b border-[#1F2B44] sticky top-0 bg-[#131E30]">
                    <tr>
                      <th className="p-2 text-left">Muaji</th>
                      <th className="p-2 text-right text-emerald-400">Butik €</th>
                      <th className="p-2 text-right text-emerald-400 hidden sm:table-cell">Fatura</th>
                      <th className="p-2 text-right text-cyan-400">Online €</th>
                      <th className="p-2 text-right text-cyan-400 hidden sm:table-cell">Porosi</th>
                      <th className="p-2 text-right text-[#FFD600]">Total</th>
                      <th className="p-2 text-right text-orange-400">TVSH</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.months.map(m => {
                      const total = (m.pos_revenue || 0) + (m.online_revenue || 0);
                      const vat = (m.pos_vat || 0) + (m.online_vat || 0);
                      return (
                        <tr key={m.key} className="border-b border-[#1F2B44]/50 hover:bg-[#0B1220]/50" data-testid={`sales-month-${m.key}`}>
                          <td className="p-2 font-mono text-xs">{m.label}</td>
                          <td className="p-2 text-right text-emerald-400">{fmtEUR(m.pos_revenue)}</td>
                          <td className="p-2 text-right text-xs text-neutral-500 hidden sm:table-cell">{m.pos_count}</td>
                          <td className="p-2 text-right text-cyan-400">{fmtEUR(m.online_revenue)}</td>
                          <td className="p-2 text-right text-xs text-neutral-500 hidden sm:table-cell">{m.online_count}</td>
                          <td className="p-2 text-right text-[#FFD600] font-bold">{fmtEUR(total)}</td>
                          <td className="p-2 text-right text-orange-400 text-xs">{fmtEUR(vat)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[#FFD600]/40 bg-[#0B1220]/50 font-bold" data-testid="sales-grand-total">
                      <td className="p-2 uppercase text-xs">Totali 12 muaj</td>
                      <td className="p-2 text-right text-emerald-400">{fmtEUR(sales.months.reduce((s,m) => s + (m.pos_revenue || 0), 0))}</td>
                      <td className="p-2 text-right text-xs text-neutral-500 hidden sm:table-cell">{sales.months.reduce((s,m) => s + (m.pos_count || 0), 0)}</td>
                      <td className="p-2 text-right text-cyan-400">{fmtEUR(sales.months.reduce((s,m) => s + (m.online_revenue || 0), 0))}</td>
                      <td className="p-2 text-right text-xs text-neutral-500 hidden sm:table-cell">{sales.months.reduce((s,m) => s + (m.online_count || 0), 0)}</td>
                      <td className="p-2 text-right text-[#FFD600]">{fmtEUR(sales.months.reduce((s,m) => s + (m.pos_revenue || 0) + (m.online_revenue || 0), 0))}</td>
                      <td className="p-2 text-right text-orange-400 text-xs">{fmtEUR(sales.months.reduce((s,m) => s + (m.pos_vat || 0) + (m.online_vat || 0), 0))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="text-[10px] text-neutral-500 mt-2 text-center">
                Butik = shitjet nga Arka (POS). Online = porositë nga faqja. TVSH kalkulohet automatikisht.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState({ products: 0, orders: 0, categories: 0, part_requests: 0, new_orders: 0, stock_value: 0, stock_qty_total: 0, customers: 0 });
  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)); }, []);
  const cards = [
    { label: "Produkte", value: stats.products, color: "text-[#FFD600]", to: "/admin/produktet" },
    { label: "Porosi", value: stats.orders, color: "text-emerald-400", to: "/admin/porosite" },
    { label: "Klientë", value: stats.customers, color: "text-cyan-400", to: "/admin/klientet" },
    { label: "Kërkesa me foto", value: stats.part_requests, color: "text-blue-400", to: "/admin/kerkesat" },
  ];
  return (
    <div>
      <h1 className="font-display text-3xl uppercase mb-4">Dashboard</h1>
      <SalesDashboardCards />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        {cards.map(c => (
          <Link key={c.label} to={c.to} data-testid={`dash-card-${c.label.toLowerCase()}`}
            className="hg-card p-4 hover:border-[#FFD600] transition-colors">
            <div className="text-xs uppercase text-neutral-400">{c.label}</div>
            <div className={`font-display text-4xl mt-1 ${c.color}`}>{c.value}</div>
          </Link>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="hg-card p-5 border-l-4 border-[#FFD600]" data-testid="stat-stock-value">
          <div className="text-xs uppercase text-neutral-400 tracking-widest">Vlera totale e stokut</div>
          <div className="font-display text-4xl sm:text-5xl mt-2 text-[#FFD600]">{fmtEUR(stats.stock_value)}</div>
          <div className="text-[11px] text-neutral-500 mt-1">Çmimi × sasia për të gjitha produktet</div>
        </div>
        <div className="hg-card p-5 border-l-4 border-emerald-400" data-testid="stat-stock-qty">
          <div className="text-xs uppercase text-neutral-400 tracking-widest">Sasia totale në stok</div>
          <div className="font-display text-4xl sm:text-5xl mt-2 text-emerald-400">{stats.stock_qty_total}</div>
          <div className="text-[11px] text-neutral-500 mt-1">Shuma e njësive e të gjitha produkteve</div>
        </div>
      </div>
    </div>
  );
};

const LowStockAlert = () => {
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const load = () => api.get("/admin/products/low-stock").then(r => setItems(r.data));
  useEffect(() => { load(); api.get("/categories").then(r => setCats(r.data)); }, []);
  const catName = (slug) => (cats.find(c => c.slug === slug)?.name) || slug || "—";

  if (items === null) return <div className="text-neutral-400 text-sm py-10 text-center">Duke ngarkuar...</div>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center">
          <AlertTriangle size={20} className="text-red-400" />
        </div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl uppercase">Alarm i stokut të ulët</h1>
          <div className="text-xs text-neutral-400">Produkte që kanë rënë nën minimumin e caktuar</div>
        </div>
        <button onClick={load} data-testid="lowstock-refresh"
          className="ml-auto text-xs uppercase tracking-wider px-3 py-2 border border-[#1F2B44] hover:border-[#FFD600] hover:text-[#FFD600]">
          Rifresko
        </button>
      </div>

      {items.length === 0 ? (
        <div className="hg-card p-8 text-center" data-testid="lowstock-empty">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <Check size={26} className="text-emerald-400" />
          </div>
          <div className="font-display text-xl">Të gjitha produktet janë në rregull</div>
          <div className="text-sm text-neutral-400 mt-1">Asnjë produkt nuk ka zbritur nën minimum.</div>
          <div className="text-[11px] text-neutral-500 mt-3">Kur mbushet stoku mbi minimum, produkti hiqet automatikisht nga kjo listë.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="lowstock-list">
          <div className="text-sm text-neutral-400">
            <span className="text-red-400 font-bold">{items.length}</span> produkt{items.length === 1 ? "" : "e"} nën minimum
          </div>
          {items.map(p => {
            const img = (p.images && p.images[0]) || "";
            const short = Math.max(0, (p.min_stock || 0) - (p.stock_qty || 0));
            return (
              <Link key={p.id} to={`/admin/produktet?edit=${p.id}`} data-testid={`lowstock-item-${p.id}`}
                className="hg-card p-3 sm:p-4 flex gap-3 items-center border-l-4 border-red-500/60 hover:border-[#FFD600]">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#131E30] border border-[#1F2B44] flex-shrink-0 flex items-center justify-center overflow-hidden">
                  {img ? <img src={mediaUrl(img)} alt="" className="w-full h-full object-cover" /> : <Package size={24} className="text-neutral-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold leading-tight line-clamp-2 text-sm">{p.name}</div>
                  <div className="text-[11px] text-neutral-400 mt-1 flex flex-wrap gap-x-3">
                    <span>{catName(p.category_slug)}</span>
                    {p.article_number && <span className="font-mono text-[#FFD600]">{p.article_number}</span>}
                  </div>
                  <div className="mt-1.5 flex items-center gap-3 text-xs">
                    <span className="text-neutral-400">Stok: <span className="text-red-400 font-bold">{p.stock_qty}</span></span>
                    <span className="text-neutral-400">Min: <span className="text-white">{p.min_stock}</span></span>
                    <span className="text-orange-400 font-medium">Mungon {short} {p.unit || "copë"}</span>
                  </div>
                </div>
                <ArrowLeft size={18} className="text-[#FFD600] rotate-180 flex-shrink-0" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CategoryPicker = ({ cats, value, onChange, allowAll = false, testid = "prod-cat" }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = cats.find(c => c.slug === value);
  const parents = cats.filter(c => !c.parent_slug).sort((a, b) => (a.order || 0) - (b.order || 0));
  const childrenOf = (slug) => cats.filter(c => c.parent_slug === slug).sort((a, b) => (a.order || 0) - (b.order || 0));
  const orphans = cats.filter(c => c.parent_slug && !parents.some(p => p.slug === c.parent_slug));
  const matchesQ = (c) => !q.trim() || c.name.toLowerCase().includes(q.trim().toLowerCase());
  const pick = (slug) => { onChange(slug); setOpen(false); setQ(""); };
  const parentSelectedName = selected && selected.parent_slug ? (cats.find(c => c.slug === selected.parent_slug)?.name || "") : "";
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid}
        className="w-full bg-[#131E30] border border-[#1F2B44] hover:border-[#FFD600] px-3 py-2 mt-1 flex items-center gap-3 text-left transition-colors">
        {selected ? (
          <>
            {selected.image ? (
              <img src={mediaUrl(selected.image)} alt="" className="w-8 h-8 object-cover bg-white flex-shrink-0" />
            ) : (
              <div className="w-8 h-8 bg-[#262626] flex items-center justify-center flex-shrink-0"><Grid3x3 size={14} className="text-neutral-500" /></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{selected.name}</div>
              {parentSelectedName && <div className="text-[10px] text-neutral-500 uppercase tracking-wider truncate">{parentSelectedName}</div>}
            </div>
            <ArrowDown size={14} className="text-neutral-400 flex-shrink-0" />
          </>
        ) : (
          <>
            <div className="w-8 h-8 bg-[#262626] flex items-center justify-center flex-shrink-0"><Grid3x3 size={14} className="text-neutral-500" /></div>
            <span className="flex-1 text-neutral-500 text-sm">{allowAll ? "Të gjitha kategoritë" : "Zgjidh kategori..."}</span>
            <ArrowDown size={14} className="text-neutral-400" />
          </>
        )}
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-start md:items-center justify-center p-2 md:p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="hg-card w-full max-w-2xl my-4 md:my-0" onClick={e => e.stopPropagation()}>
            <div className="p-3 border-b border-[#1F2B44] flex items-center gap-2 sticky top-0 bg-[#131E30] z-10">
              <SearchIcon size={16} className="text-neutral-400 flex-shrink-0" />
              <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Kërko kategori..."
                data-testid="cat-picker-search"
                className="flex-1 bg-transparent outline-none text-sm" />
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-[#262626] flex-shrink-0"><X size={16} /></button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto p-2">
              {allowAll && !q.trim() && (
                <button type="button" onClick={() => pick("")} data-testid="cat-pick-all"
                  className={`w-full flex items-center gap-3 p-2.5 mb-3 border-l-4 ${value === "" ? "border-[#FFD600] bg-[#FFD600]/10" : "border-neutral-600 bg-[#262626]/40 hover:bg-[#262626]"}`}>
                  <div className="w-10 h-10 bg-[#131E30] flex items-center justify-center flex-shrink-0"><Grid3x3 size={16} className="text-white" /></div>
                  <div className="flex-1 text-left">
                    <div className="text-sm font-bold uppercase tracking-wider">Të gjitha kategoritë</div>
                    <div className="text-[10px] text-neutral-500">Shfaq krejt produktet</div>
                  </div>
                  {value === "" && <Check size={16} className="text-[#FFD600] flex-shrink-0" />}
                </button>
              )}
              {parents.map(parent => {
                const kids = childrenOf(parent.slug).filter(matchesQ);
                const parentMatches = matchesQ(parent);
                if (!parentMatches && kids.length === 0) return null;
                return (
                  <div key={parent.slug} className="mb-3">
                    <button type="button" onClick={() => pick(parent.slug)} data-testid={`cat-pick-${parent.slug}`}
                      className={`w-full flex items-center gap-3 p-2.5 border-l-4 ${value === parent.slug ? "border-[#FFD600] bg-[#FFD600]/10" : "border-[#FFD600]/40 bg-[#262626]/40 hover:bg-[#262626]"}`}>
                      {parent.image ? (
                        <img src={mediaUrl(parent.image)} alt="" className="w-10 h-10 object-cover bg-white flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 bg-[#131E30] flex items-center justify-center flex-shrink-0"><Grid3x3 size={16} className="text-[#FFD600]" /></div>
                      )}
                      <div className="flex-1 text-left">
                        <div className="text-sm font-bold uppercase tracking-wider text-[#FFD600]">{parent.name}</div>
                        <div className="text-[10px] text-neutral-500">{childrenOf(parent.slug).length > 0 ? `${childrenOf(parent.slug).length} nën-kategori` : "Kategori kryesore"}</div>
                      </div>
                      {value === parent.slug && <Check size={16} className="text-[#FFD600] flex-shrink-0" />}
                    </button>
                    {kids.length > 0 && (
                      <div className="ml-4 mt-1 space-y-1 border-l border-[#1F2B44] pl-2">
                        {kids.map(kid => (
                          <button key={kid.slug} type="button" onClick={() => pick(kid.slug)} data-testid={`cat-pick-${kid.slug}`}
                            className={`w-full flex items-center gap-2 p-2 hover:bg-[#262626] ${value === kid.slug ? "bg-[#FFD600]/10 border border-[#FFD600]/40" : "border border-transparent"}`}>
                            {kid.image ? (
                              <img src={mediaUrl(kid.image)} alt="" className="w-7 h-7 object-cover bg-white flex-shrink-0" />
                            ) : (
                              <div className="w-7 h-7 bg-[#262626] flex items-center justify-center flex-shrink-0"><Grid3x3 size={12} className="text-neutral-500" /></div>
                            )}
                            <span className="flex-1 text-left text-sm">{kid.name}</span>
                            {value === kid.slug && <Check size={14} className="text-[#FFD600] flex-shrink-0" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {orphans.filter(matchesQ).length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 px-2 py-1">Të tjera</div>
                  {orphans.filter(matchesQ).map(o => (
                    <button key={o.slug} type="button" onClick={() => pick(o.slug)} data-testid={`cat-pick-${o.slug}`}
                      className={`w-full flex items-center gap-2 p-2 hover:bg-[#262626] ${value === o.slug ? "bg-[#FFD600]/10" : ""}`}>
                      {o.image ? <img src={mediaUrl(o.image)} alt="" className="w-7 h-7 object-cover bg-white flex-shrink-0" /> : <div className="w-7 h-7 bg-[#262626] flex-shrink-0" />}
                      <span className="flex-1 text-left text-sm">{o.name}</span>
                      {value === o.slug && <Check size={14} className="text-[#FFD600] flex-shrink-0" />}
                    </button>
                  ))}
                </div>
              )}
              {cats.filter(matchesQ).length === 0 && (
                <div className="text-center py-8 text-neutral-500 text-sm">Nuk u gjet asnjë kategori</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Products = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);
  const [stockInfo, setStockInfo] = useState({ locations: [], total_qty: 0 });
  const [pendingLoc, setPendingLoc] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [filterQ, setFilterQ] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [diameterGroups, setDiameterGroups] = useState({});
  const [sizeCodeGroups, setSizeCodeGroups] = useState({});
  const empty = { name: "", brand: "", model: "", sku: "", article_number: "", oem_number: "", category_slug: "", description: "", specifications: {}, price: 0, vat_rate: 18, discount_price: null, stock_qty: 0, min_stock: 0, unit: "copë", size: "", size_code: "", images: [], related_ids: [], shelf_location: "", stock_article_number: "", configurator_role: "", badge_text: "" };

  const load = () => api.get("/products?limit=500").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/categories").then(r => setCats(r.data));
    api.get("/settings").then(r => {
      if (r.data?.diameters) setDiameterGroups(r.data.diameters);
      if (r.data?.size_codes) setSizeCodeGroups(r.data.size_codes);
    }).catch(() => {});
  }, []);

  const filtered = items.filter(p => {
    if (filterCat && p.category_slug !== filterCat) return false;
    if (filterQ.trim()) {
      const q = filterQ.trim().toLowerCase();
      const hay = [p.name, p.brand, p.model, p.sku, p.article_number, p.oem_number, p.stock_article_number, p.shelf_location].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  useEffect(() => {
    if (edit && edit.id) {
      api.get(`/admin/products/${edit.id}/stock`).then(r => setStockInfo(r.data)).catch(() => setStockInfo({ locations: [], total_qty: 0 }));
    } else {
      setStockInfo({ locations: [], total_qty: 0 });
    }
    setPendingLoc(null);
  }, [edit?.id]);

  const save = async () => {
    try {
      let productId = edit.id;
      if (edit.id) {
        await api.put(`/admin/products/${edit.id}`, edit);
      } else {
        const r = await api.post("/admin/products", edit);
        productId = r.data.id;
      }
      // Register pending stock location if specified (auto uses "Sasia në stok" value)
      const qtyToRegister = Number(edit.stock_qty || 0);
      if (pendingLoc && qtyToRegister > 0 && productId) {
        try {
          await api.post(`/admin/products/${productId}/stock/set`, {
            qty: qtyToRegister,
            cabinet: pendingLoc.cabinet, drawer: pendingLoc.drawer, position: pendingLoc.position,
            note: edit.id ? "Vendosje shtesë nga forma" : "Vendosje fillestare nga forma",
          });
          toast.success(`U ruajt + ${qtyToRegister} copë në ${pendingLoc.cabinet}-${pendingLoc.drawer}-${pendingLoc.position}`);
        } catch (e) {
          toast.error(`Ruajtja e produktit u krye, por vendosja në stok dështoi: ${e.response?.data?.detail || "gabim"}`);
        }
      } else {
        toast.success("U ruajt");
      }
      setEdit(null); load();
    } catch { toast.error("Gabim gjatë ruajtjes"); }
  };
  const del = async (id) => {
    if (!window.confirm("Fshi produktin?")) return;
    await api.delete(`/admin/products/${id}`); load(); toast.success("U fshi");
  };
  const upload = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setEdit({ ...edit, images: [...(edit.images || []), r.data.url] });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-3xl uppercase">Produktet</h1>
        <button onClick={() => setEdit({ ...empty })} data-testid="admin-add-product" className="hg-btn-primary px-4 py-2 uppercase text-xs inline-flex items-center gap-1"><Plus size={14} /> Shto</button>
      </div>
      <div className="hg-card p-3 mb-3 grid sm:grid-cols-2 gap-2" data-testid="products-filters">
        <input
          value={filterQ} onChange={e => setFilterQ(e.target.value)}
          data-testid="products-filter-search"
          placeholder="Kërko sipas emrit, SKU, artikullit, markës..."
          className="h-10 bg-[#131E30] border border-[#1F2B44] px-3"
        />
        <div className="h-10 md:h-auto">
          <CategoryPicker cats={cats} value={filterCat} onChange={setFilterCat} allowAll={true} testid="products-filter-cat" />
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-2">{filtered.length} nga {items.length} produkte</div>
      <div className="hg-card overflow-x-auto -webkit-overflow-scrolling-touch">
        <table className="w-max min-w-full text-sm">
          <thead className="text-xs uppercase text-neutral-400 border-b border-[#1F2B44]">
            <tr><th className="p-3 text-left whitespace-nowrap">Foto</th><th className="p-3 text-left whitespace-nowrap">Emri</th><th className="p-3 text-left whitespace-nowrap">Marka</th><th className="p-3 text-left whitespace-nowrap">Kategori</th><th className="p-3 text-left whitespace-nowrap">Art. Nr.</th><th className="p-3 text-left whitespace-nowrap">Plac</th><th className="p-3 text-right whitespace-nowrap">Çmimi</th><th className="p-3 text-right whitespace-nowrap">Stok</th><th className="whitespace-nowrap"></th></tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id} className="border-b border-[#1F2B44]">
                <td className="p-2 whitespace-nowrap">
                  <div className="w-12 h-12 bg-white overflow-hidden flex items-center justify-center">
                    {p.images && p.images[0]
                      ? <img src={mediaUrl(p.images[0])} alt="" className="w-full h-full object-contain" />
                      : <Package size={18} className="text-neutral-400" />}
                  </div>
                </td>
                <td className="p-3 whitespace-nowrap max-w-[220px] truncate">{p.name}</td>
                <td className="p-3 text-neutral-400 whitespace-nowrap">{p.brand}</td>
                <td className="p-3 text-neutral-400 whitespace-nowrap">{p.category_slug}</td>
                <td className="p-3 text-[#FFD600] font-mono text-xs whitespace-nowrap">{p.article_number || "—"}</td>
                <td className="p-3 text-neutral-300 font-mono text-xs whitespace-nowrap">
                  {p.shelf_location
                    ? p.shelf_location.split(",").map(s => s.trim()).filter(Boolean).map((loc, i, arr) => (
                        <Link key={i} to={`/admin/dollapet?loc=${encodeURIComponent(loc)}`} data-testid={`shelf-link-${p.id}-${i}`}
                          className="text-[#FFD600] hover:underline hover:bg-[#FFD600]/10 px-1 py-0.5 rounded transition-colors">
                          {loc}{i < arr.length - 1 ? ", " : ""}
                        </Link>
                      ))
                    : "—"}
                </td>
                <td className="p-3 text-right text-[#FFD600] whitespace-nowrap">{fmtEUR(p.price)}</td>
                <td className="p-3 text-right whitespace-nowrap">{p.stock_qty}</td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <button onClick={() => setEdit({ ...p })} className="p-1.5 hover:bg-[#262626]"><Edit3 size={14} /></button>
                  <button onClick={() => del(p.id)} className="p-1.5 hover:bg-[#262626] text-red-400"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-neutral-500 text-sm">Asnjë produkt nuk përputhet me filtrat</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="md:hidden text-[10px] text-neutral-500 mt-1 text-center">← Rrëshqit majtas/djathtas për të parë të gjitha kolonat →</div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="hg-card max-w-2xl w-full max-h-[90vh] overflow-auto p-5" onClick={e => e.stopPropagation()}>
            <div className="font-display text-2xl uppercase mb-4">{edit.id ? "Ndrysho" : "Shto"} produkt</div>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase text-neutral-400 tracking-wider">Emri i produktit *</label>
                <input placeholder="p.sh. Tub hidraulik 2SN 1/2" value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} data-testid="prod-name" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase text-neutral-400 tracking-wider">Marka</label>
                  <input placeholder="p.sh. Danfoss" value={edit.brand || ""} onChange={e => setEdit({ ...edit, brand: e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400 tracking-wider">Materiali</label>
                  <input placeholder="p.sh. Çelik, Gomë, Alumin" value={edit.model || ""} onChange={e => setEdit({ ...edit, model: e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400 tracking-wider">Made in</label>
                  <input placeholder="p.sh. Italy, Germany, Turkey" value={edit.sku || ""} onChange={e => setEdit({ ...edit, sku: e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400 tracking-wider">OEM Nr.</label>
                  <input value={edit.oem_number || ""} onChange={e => setEdit({ ...edit, oem_number: e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs uppercase text-neutral-400 tracking-wider">Art. Nr.</label>
                  <div className="mt-1 flex items-stretch border border-[#1F2B44] focus-within:border-[#FFD600]">
                    <span className="px-3 flex items-center bg-[#FFD600]/10 text-[#FFD600] font-mono font-bold text-sm border-r border-[#1F2B44] select-none">HG</span>
                    <input value={(edit.article_number || "").replace(/^HG-?/i, "")}
                      onChange={e => {
                        const digits = e.target.value.replace(/\D/g, "");
                        setEdit({ ...edit, article_number: digits ? `HG${digits}` : "" });
                      }}
                      placeholder="28262678" data-testid="prod-artnr"
                      inputMode="numeric" pattern="[0-9]*"
                      className="flex-1 bg-[#131E30] px-3 py-2 font-mono outline-none" />
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-1">Prefixi <span className="text-[#FFD600] font-mono">HG</span> është i bllokuar. Shkruaj vetëm numrat.</div>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase text-neutral-400 tracking-wider">Kategoria *</label>
                <CategoryPicker cats={cats} value={edit.category_slug} onChange={slug => setEdit({ ...edit, category_slug: slug })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase text-neutral-400 tracking-wider">Çmimi (€) *</label>
                  <input type="number" step="0.01" placeholder="0.00" value={edit.price} onChange={e => setEdit({ ...edit, price: +e.target.value })} data-testid="prod-price" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400 tracking-wider">TVSH (%) *</label>
                  <select value={edit.vat_rate ?? 18} onChange={e => setEdit({ ...edit, vat_rate: +e.target.value })} data-testid="prod-vat"
                    className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1">
                    {Array.from({length: 21}, (_, i) => 10 + i).map(v => (
                      <option key={v} value={v}>{v}%</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-neutral-500 mt-1">Zgjidh përqindjen e TVSH (10% – 30%)</div>
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400 tracking-wider">Çmimi me zbritje (€)</label>
                  <input type="number" step="0.01" placeholder="opsional" value={edit.discount_price ?? ""} onChange={e => setEdit({ ...edit, discount_price: e.target.value === "" ? null : +e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400 tracking-wider">Sasia në stok *</label>
                  <input type="number" placeholder="0" value={edit.stock_qty} onChange={e => setEdit({ ...edit, stock_qty: +e.target.value })} data-testid="prod-stock" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400 tracking-wider">Minimumi i stokut</label>
                  <input type="number" placeholder="0" value={edit.min_stock ?? 0} onChange={e => setEdit({ ...edit, min_stock: +e.target.value })} data-testid="prod-min-stock" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                  <div className="text-[10px] text-neutral-500 mt-1">Alarm kur sasia zbret ≤ minimum. Vendos 0 për të çaktivizuar.</div>
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400 tracking-wider">Njësia matëse *</label>
                  <select value={edit.unit || "copë"} onChange={e => setEdit({ ...edit, unit: e.target.value })} data-testid="prod-unit" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1">
                    <option value="copë">copë</option>
                    <option value="metër">metër</option>
                    <option value="set">set</option>
                    <option value="litër">litër</option>
                    <option value="kg">kg</option>
                    <option value="paketë">paketë</option>
                  </select>
                </div>
                <div className="col-span-2 hg-card p-3 border-l-4 border-[#FFD600] flex items-center justify-between bg-[#FFD600]/5" data-testid="prod-total-value">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">Totali i stokut</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">{Number(edit.stock_qty || 0)} × {fmtEUR(Number(edit.price || 0))}</div>
                  </div>
                  <div className="font-display text-3xl text-[#FFD600]">
                    {fmtEUR(Number(edit.stock_qty || 0) * Number(edit.price || 0))}
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="text-xs uppercase text-neutral-400 tracking-wider">Diametri</label>
                  <div className="flex gap-2 mt-1">
                    <select value={edit.size || ""} onChange={e => setEdit({ ...edit, size: e.target.value })} data-testid="prod-size" className="flex-1 bg-[#131E30] border border-[#1F2B44] px-3 py-2">
                      <option value="">— pa diametër —</option>
                      {edit.size && !Object.values(diameterGroups).flat().includes(edit.size) && (
                        <option value={edit.size}>{edit.size} (vlerë e vjetër)</option>
                      )}
                      {Object.entries(diameterGroups).map(([groupLabel, values]) => (
                        <optgroup key={groupLabel} label={`── ${groupLabel} ──`}>
                          {(values || []).map(v => <option key={v} value={v}>{v}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    {edit.size && (
                      <button type="button" onClick={() => setEdit({ ...edit, size: "" })}
                        data-testid="prod-size-clear"
                        className="px-3 border border-[#1F2B44] hover:border-red-500 hover:text-red-400 text-xs uppercase" title="Pastro">×</button>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="text-xs uppercase text-neutral-400 tracking-wider">Madhësia (Kodi / Modeli)</label>
                  <div className="flex gap-2 mt-1">
                    <select value={edit.size_code || ""} onChange={e => setEdit({ ...edit, size_code: e.target.value })} data-testid="prod-size-code" className="flex-1 bg-[#131E30] border border-[#1F2B44] px-3 py-2">
                      <option value="">— pa madhësi —</option>
                      {edit.size_code && !Object.values(sizeCodeGroups).flat().includes(edit.size_code) && (
                        <option value={edit.size_code}>{edit.size_code} (vlerë e vjetër)</option>
                      )}
                      {Object.entries(sizeCodeGroups).map(([groupLabel, values]) => (
                        <optgroup key={groupLabel} label={`── ${groupLabel} ──`}>
                          {(values || []).map(v => <option key={v} value={v}>{v}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    {edit.size_code && (
                      <button type="button" onClick={() => setEdit({ ...edit, size_code: "" })}
                        data-testid="prod-size-code-clear"
                        className="px-3 border border-[#1F2B44] hover:border-red-500 hover:text-red-400 text-xs uppercase" title="Pastro">×</button>
                    )}
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-1">Kodi ose modeli specifik i produktit (p.sh. HYLSA AD5, KOPLINGA BSC, SHTRËNGUES 25–28).</div>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase text-neutral-400 tracking-wider">Etiketa në kartelë (opsional)</label>
                <input value={edit.badge_text || ""} onChange={e => setEdit({ ...edit, badge_text: e.target.value })}
                  placeholder="p.sh. 1/4, I RI, ZBRITJE — bosh = 'Në stok'" data-testid="prod-badge"
                  maxLength={20}
                  className="w-full bg-[#131E30] border border-[#1F2B44] focus:border-[#FFD600] px-3 py-2 mt-1 outline-none" />
                <div className="text-[10px] text-neutral-500 mt-1">Kjo etiketë shfaqet në këndin lart-majtas të kartelës së produktit në vend të "Në stok". Lëre bosh për të shfaqur statusin automatik të stokut.</div>
              </div>
              <div>
                <label className="text-xs uppercase text-neutral-400 tracking-wider">Përshkrimi</label>
                <textarea placeholder="Shkruaj detajet e produktit..." value={edit.description || ""} onChange={e => setEdit({ ...edit, description: e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" rows={3} />
              </div>
              <div className="border-2 border-[#25D366]/30 bg-[#25D366]/5 p-3 mt-1">
                <div className="text-xs uppercase text-[#25D366] tracking-widest mb-2 font-bold">⚙️ Roli në Konfigurator</div>
                <div className="text-[11px] text-neutral-400 mb-2">Nëse zgjedh një rol, produkti shfaqet automatikisht në hapin përkatës të konfiguratorit — pa nevojë ta shtosh dy herë.</div>
                <select value={edit.configurator_role || ""} onChange={e => setEdit({ ...edit, configurator_role: e.target.value })} data-testid="prod-cfgrole"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2">
                  <option value="">— asnjë (produkt normal) —</option>
                  <option value="hose">🟡 Gyp (Hapi 1)</option>
                  <option value="ferrule">🟡 Hylsa (Hapi 2)</option>
                  <option value="coupling">🟡 Kopllinga (Hapi 3-4)</option>
                </select>
              </div>
              <div className="border-2 border-[#FFD600]/30 bg-[#FFD600]/5 p-3 mt-1">
                <div className="text-xs uppercase text-[#FFD600] tracking-widest mb-2 font-bold flex items-center gap-2"><MapPin size={14} /> Vendi në stok</div>
                {stockInfo.locations.length > 0 && (
                  <div className="mb-3 space-y-1">
                    <div className="text-[11px] text-neutral-400">Lokacionet aktuale ({stockInfo.total_qty} copë total):</div>
                    {stockInfo.locations.map((l, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-[#131E30] border border-[#1F2B44] px-2 py-1.5">
                        <span className="font-mono"><b className="text-[#FFD600]">{l.cabinet}-{l.drawer}-{l.position}</b></span>
                        <span className="text-emerald-400 font-bold">{l.qty} cp</span>
                      </div>
                    ))}
                  </div>
                )}
                {!pendingLoc ? (
                  <button type="button" onClick={() => setShowPicker(true)} data-testid="pick-location-btn"
                    className="w-full h-12 border-2 border-dashed border-[#FFD600] text-[#FFD600] uppercase text-xs inline-flex items-center justify-center gap-2 hover:bg-[#FFD600]/10">
                    <MapPin size={14} /> {stockInfo.locations.length > 0 ? "Shto lokacion tjetër" : "Zgjidh vendin në stok"}
                  </button>
                ) : (
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between bg-[#131E30] border border-[#FFD600] px-3 py-2 text-sm">
                      <span className="font-mono text-[#FFD600]">{pendingLoc.cabinet}-{pendingLoc.drawer}-{pendingLoc.position}</span>
                      <button type="button" onClick={() => setPendingLoc(null)} className="text-neutral-400 hover:text-red-400"><X size={14} /></button>
                    </div>
                    <div className="text-[11px] text-neutral-300 bg-black/40 border border-[#1F2B44] px-2 py-1.5">
                      Sasia <b className="text-[#FFD600]">{Number(edit.stock_qty || 0)} {edit.unit || "copë"}</b> nga fusha <i>"Sasia në stok"</i> do të regjistrohet automatikisht në këtë lokacion kur të klikohet <b className="text-[#FFD600]">Ruaj</b>.
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs uppercase text-neutral-400 tracking-wider">Imazhet</label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {(edit.images || []).map((im, i) => (
                    <div key={i} className="relative w-20 h-20">
                      <img src={mediaUrl(im)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setEdit({ ...edit, images: edit.images.filter((_, j) => j !== i) })} className="absolute -top-2 -right-2 bg-red-600 text-white w-5 h-5 text-xs">×</button>
                    </div>
                  ))}
                  <label className="w-20 h-20 border-2 border-dashed border-[#1F2B44] flex items-center justify-center cursor-pointer hover:border-[#FFD600]">
                    <Plus size={20} />
                    <input type="file" onChange={upload} className="hidden" />
                  </label>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={save} data-testid="admin-save-product" className="hg-btn-primary flex-1 py-2 uppercase text-sm">Ruaj</button>
              <button onClick={() => setEdit(null)} className="border border-[#1F2B44] px-4 py-2 text-sm">Anulo</button>
            </div>
          </div>
        </div>
      )}

      {showPicker && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-2 sm:p-4" onClick={() => setShowPicker(false)}>
          <div className="bg-[#0B1220] border border-[#1F2B44] w-full max-w-5xl max-h-[95vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-black/95 backdrop-blur border-b border-[#1F2B44] px-4 py-3 flex items-center justify-between z-10">
              <div className="font-display text-lg uppercase">Zgjidh vendin në stok</div>
              <button onClick={() => setShowPicker(false)}><X /></button>
            </div>
            <div className="p-4">
              <LocationPicker
                onSelect={(loc) => setPendingLoc(loc)}
                currentProductId={edit?.id}
                blockOccupied={true}
              />
              <div className="flex gap-2 mt-4">
                <button onClick={() => setShowPicker(false)} disabled={!pendingLoc}
                  data-testid="confirm-location-btn"
                  className="hg-btn-primary flex-1 h-12 uppercase text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40">
                  {pendingLoc ? `Konfirmo ${pendingLoc.cabinet}-${pendingLoc.drawer}-${pendingLoc.position}` : "Zgjidh një pozicion"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Orders = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/orders").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status }); load(); toast.success("U përditësua");
  };
  const deleteOrder = async (id, orderNum) => {
    if (!window.confirm(`A jeni i sigurt që doni të fshini porosinë #${orderNum}?\nKjo veprim s'mund të kthehet mbrapa.`)) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success("Porosia u fshi");
      load();
    } catch { toast.error("Gabim gjatë fshirjes"); }
  };
  const fmtDate = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("sq-AL", { day: "2-digit", month: "2-digit", year: "numeric" }) + " " + d.toLocaleTimeString("sq-AL", { hour: "2-digit", minute: "2-digit" });
    } catch { return "-"; }
  };
  return (
    <div>
      <h1 className="font-display text-3xl uppercase mb-4">Porositë</h1>
      <div className="hg-card overflow-x-auto -webkit-overflow-scrolling-touch">
        <table className="w-max min-w-full text-sm">
          <thead className="text-xs uppercase text-neutral-400 border-b border-[#1F2B44]">
            <tr>
              <th className="p-3 text-left whitespace-nowrap">Nr.</th>
              <th className="p-3 text-left whitespace-nowrap">Data</th>
              <th className="p-3 text-left whitespace-nowrap">Klienti</th>
              <th className="p-3 text-left whitespace-nowrap">Telefon</th>
              <th className="p-3 text-right whitespace-nowrap">Totali</th>
              <th className="p-3 text-left whitespace-nowrap">Statusi</th>
              <th className="whitespace-nowrap"></th>
              <th className="whitespace-nowrap"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(o => (
              <tr key={o.id} className="border-b border-[#1F2B44]">
                <td className="p-3 font-mono text-xs whitespace-nowrap">{o.order_number}</td>
                <td className="p-3 text-neutral-400 text-xs whitespace-nowrap" data-testid={`order-date-${o.id}`}>{fmtDate(o.created_at)}</td>
                <td className="p-3 whitespace-nowrap">{o.first_name} {o.last_name}</td>
                <td className="p-3 text-neutral-400 whitespace-nowrap">{o.phone}</td>
                <td className="p-3 text-right text-[#FFD600] whitespace-nowrap">{fmtEUR(o.total)}</td>
                <td className="p-3 whitespace-nowrap">
                  <select value={o.status} onChange={e => setStatus(o.id, e.target.value)} className="bg-[#131E30] border border-[#1F2B44] px-2 py-1 text-xs">
                    {ORDER_STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="p-3 whitespace-nowrap"><Link to={`/admin/porosite/${o.id}`} data-testid={`order-detail-${o.id}`} className="text-xs uppercase text-[#FFD600] hover:underline">Detaje</Link></td>
                <td className="p-3 whitespace-nowrap">
                  <button onClick={() => deleteOrder(o.id, o.order_number)} data-testid={`order-delete-${o.id}`}
                    className="text-xs uppercase text-red-400 hover:text-red-300 hover:underline inline-flex items-center gap-1">
                    <Trash2 size={12} /> Fshi
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden text-[10px] text-neutral-500 mt-1 text-center">← Rrëshqit majtas/djathtas për të parë të gjitha kolonat →</div>
    </div>
  );
};

const STATUS_STYLE = {
  "E re": "bg-orange-500/15 text-orange-300 border-orange-500/40",
  "Në pritje": "bg-orange-500/15 text-orange-300 border-orange-500/40",
  "Në përpunim": "bg-blue-500/15 text-blue-300 border-blue-500/40",
  "Duke u përgatitur": "bg-purple-500/15 text-purple-300 border-purple-500/40",
  "Dërguar": "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  "Përfunduar": "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  "Anuluar": "bg-red-500/15 text-red-300 border-red-500/40",
};

const OrderDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/orders/${id}`).then(r => setOrder(r.data)).catch(() => toast.error("Porosia nuk u gjet"));
    api.get("/settings").then(r => setSettings(r.data)).catch(() => {});
  }, [id]);

  if (!order) return <div className="text-neutral-400 text-sm py-10 text-center">Duke ngarkuar...</div>;

  const items = order.items || [];
  const subtotal = Number(order.subtotal || 0);
  const delivery = Number(order.delivery || 0);
  const total = Number(order.total || 0);
  // VAT per item using each item's vat_rate (Kosovo prices are VAT-inclusive)
  const vatIncluded = +items.reduce((s, it) => {
    const line = Number(it.unit_price || 0) * Number(it.quantity || 0);
    const rate = Number(it.vat_rate ?? 18);
    return s + (line - line / (1 + rate / 100));
  }, 0).toFixed(2);
  const subtotalNoVat = +(subtotal - vatIncluded).toFixed(2);
  const vatRates = [...new Set(items.map(it => Number(it.vat_rate ?? 18)))];
  const vatLabel = vatRates.length === 1 ? `TVSH (${vatRates[0]}%)` : "TVSH";

  const statusClass = STATUS_STYLE[order.status] || "bg-neutral-700/30 text-neutral-300 border-neutral-500/40";

  const accept = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/orders/${order.id}/status`, { status: "Në përpunim" });
      setOrder({ ...order, status: "Në përpunim" });
      toast.success("Porosia u pranua");
    } catch { toast.error("Gabim"); }
    setSaving(false);
  };

  const printOrder = () => {
    document.body.classList.add("printing-admin-order");
    setTimeout(() => {
      window.print();
      document.body.classList.remove("printing-admin-order");
    }, 100);
  };

  const qrFor = (it) => {
    const data = it.product_id ? `${window.location.origin}/produkt/${it.product_id}` : `HG-ORDER:${order.order_number}:${it.name}`;
    return `${API}/admin/qr?data=${encodeURIComponent(data)}&size=6`;
  };

  return (
    <div className="max-w-3xl mx-auto -mt-2 print:mt-0">
      {/* Back button */}
      <button onClick={() => nav("/admin/porosite")} data-testid="order-back"
        className="mb-4 inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-[#FFD600] print:hidden">
        <ArrowLeft size={18} /> Kthehu te porositë
      </button>

      {/* Order header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          {order.company_name && (
            <div className="text-[10px] uppercase tracking-widest text-[#FFD600] font-bold mb-0.5">🏢 {order.company_name}</div>
          )}
          <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold">Porosia</div>
          <div className="font-display text-2xl sm:text-3xl mt-1 break-all" data-testid="order-number">#{order.order_number}</div>
        </div>
        <div className={`text-xs uppercase tracking-widest font-bold px-3 py-2 border rounded ${statusClass}`} data-testid="order-status-badge">
          {order.status}
        </div>
      </div>

      {/* Customer */}
      <div className="hg-card p-4 mb-4">
        <div className="flex items-center gap-2 text-[#FFD600] mb-3">
          <UserIcon size={18} />
          <div className="text-xs uppercase tracking-widest font-bold">Klienti</div>
        </div>
        {order.company_name && (
          <div className="mb-3 pb-3 border-b border-[#FFD600]/30">
            <div className="text-[10px] uppercase tracking-widest text-[#FFD600] font-bold">🏢 Firma</div>
            <div className="font-display text-lg" data-testid="order-company">{order.company_name}</div>
          </div>
        )}
        <div className="grid grid-cols-[90px_1fr] gap-y-1.5 text-sm">
          <div className="text-neutral-400">Emri:</div><div>{order.first_name} {order.last_name}</div>
          <div className="text-neutral-400">Telefoni:</div><div><a href={`tel:${order.phone}`} className="hover:text-[#FFD600]">{order.phone}</a></div>
          {order.email && (<><div className="text-neutral-400">Email:</div><div className="break-all"><a href={`mailto:${order.email}`} className="hover:text-[#FFD600]">{order.email}</a></div></>)}
          <div className="text-neutral-400">Adresa:</div><div>{order.address}</div>
          <div className="text-neutral-400">Qyteti:</div><div>{order.city}</div>
          {order.country && (<><div className="text-neutral-400">Shteti:</div><div>{order.country}</div></>)}
          {order.note && (<><div className="text-neutral-400">Shënim:</div><div>{order.note}</div></>)}
        </div>
      </div>

      {/* Items */}
      <div className="mb-3">
        <div className="text-xs uppercase tracking-widest font-bold mb-3">Artikujt e porosisë</div>
        <div className="space-y-3">
          {items.map((it, i) => {
            const lineTotal = Number(it.unit_price || 0) * Number(it.quantity || 0);
            return (
              <div key={i} className="hg-card p-3 sm:p-4 flex gap-3" data-testid={`order-item-${i}`}>
                {/* Product image */}
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-[#131E30] border border-[#1F2B44] flex-shrink-0 flex items-center justify-center overflow-hidden">
                  {it.image ? (
                    <img src={mediaUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />
                  ) : (
                    <Package size={28} className="text-neutral-600" />
                  )}
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0 text-sm">
                  <div className="font-bold leading-tight line-clamp-2">{it.name}</div>
                  {it.product_id && (
                    <div className="mt-1 text-xs text-neutral-400">Kod: <span className="text-[#FFD600] font-mono">HG-{String(it.product_id).slice(-6).toUpperCase()}</span></div>
                  )}
                  {it.configuration && (
                    <div className="mt-1 text-[11px] text-neutral-500 line-clamp-1">Konfigurim i personalizuar</div>
                  )}
                  <div className="mt-1 text-xs text-neutral-400">Sasia: <span className="text-white font-medium">{it.quantity}</span></div>
                  <div className="text-xs text-neutral-400">Çmimi për copë: <span className="text-white font-medium">{fmtEUR(it.unit_price)}</span></div>
                  <div className="text-xs text-neutral-400 mt-0.5">Totali i artikullit: <span className="text-[#FFD600] font-bold">{fmtEUR(lineTotal)}</span></div>
                </div>
                {/* QR */}
                <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 bg-white p-1.5 rounded" data-testid={`order-item-qr-${i}`}>
                  <img src={qrFor(it)} alt="QR" className="w-full h-full object-contain" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Totals */}
      <div className="hg-card p-4 sm:p-5 mb-4">
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-neutral-300">Nëntotali:</span><span className="font-medium">{fmtEUR(subtotalNoVat)}</span></div>
          <div className="flex justify-between"><span className="text-neutral-300">{vatLabel}:</span><span className="font-medium">{fmtEUR(vatIncluded)}</span></div>
          <div className="flex justify-between"><span className="text-neutral-300">Transporti:</span><span className="font-medium">{fmtEUR(delivery)}</span></div>
        </div>
        <div className="mt-3 pt-3 border-t border-[#1F2B44] flex justify-between items-baseline">
          <span className="text-sm uppercase tracking-widest font-bold">Totali:</span>
          <span className="font-display text-3xl sm:text-4xl text-[#FFD600]" data-testid="order-total">{fmtEUR(total)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-3 mb-4 print:hidden">
        <button onClick={accept} disabled={saving || order.status !== "E re"} data-testid="order-accept"
          className="hg-btn-primary w-full py-3.5 uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
          <Check size={18} /> {order.status === "E re" ? (saving ? "Duke u pranuar..." : "Prano porosinë") : "Porosia u pranua"}
        </button>
        <button onClick={printOrder} data-testid="order-print"
          className="w-full py-3.5 uppercase text-sm border border-[#1F2B44] hover:border-[#FFD600] hover:text-[#FFD600] flex items-center justify-center gap-2">
          <Printer size={18} /> Printo porosinë
        </button>
      </div>

      {/* Status update */}
      <div className="hg-card p-4 mb-4 flex items-center gap-3">
        <Clock size={20} className="text-[#FFD600] flex-shrink-0" />
        <div className="text-xs uppercase tracking-widest font-bold">Statusi</div>
        <select value={order.status} onChange={async (e) => {
          const v = e.target.value;
          try { await api.put(`/admin/orders/${order.id}/status`, { status: v }); setOrder({ ...order, status: v }); toast.success("U përditësua"); }
          catch { toast.error("Gabim"); }
        }} data-testid="order-status-select"
          className={`ml-auto bg-[#131E30] border px-3 py-2 text-xs uppercase tracking-wider rounded ${statusClass}`}>
          {ORDER_STATUSES.map(s => <option key={s} className="bg-[#131E30] text-white">{s}</option>)}
        </select>
      </div>

      {/* Shop footer info */}
      <div className="hg-card p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 text-xs sm:text-sm print:mt-6">
        <img src="/logo-hidrogv.png" alt="HidroGV" className="h-12 w-auto" />
        <div className="grid gap-1 flex-1">
          {settings.address && <div className="flex gap-2 text-neutral-300"><MapPin size={14} className="text-[#FFD600] mt-0.5" /><span className="whitespace-pre-line">{settings.address}</span></div>}
          {settings.contact_phone && <div className="flex gap-2 text-neutral-300"><PhoneIcon size={14} className="text-[#FFD600] mt-0.5" /><a href={`tel:${settings.contact_phone}`}>{settings.contact_phone}</a></div>}
          {settings.email && <div className="flex gap-2 text-neutral-300"><Mail size={14} className="text-[#FFD600] mt-0.5" /><a href={`mailto:${settings.email}`} className="break-all">{settings.email}</a></div>}
        </div>
        <div className="flex gap-2 print:hidden">
          {settings.facebook_url && <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="w-8 h-8 flex items-center justify-center border border-[#FFD600] text-[#FFD600] rounded-full hover:bg-[#FFD600] hover:text-black transition-colors" data-testid="ord-soc-fb"><Facebook size={14} /></a>}
          {settings.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="w-8 h-8 flex items-center justify-center border border-[#FFD600] text-[#FFD600] rounded-full hover:bg-[#FFD600] hover:text-black transition-colors" data-testid="ord-soc-ig"><Instagram size={14} /></a>}
          {settings.whatsapp_number && <a href={`https://wa.me/${(settings.whatsapp_number || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="w-8 h-8 flex items-center justify-center border border-[#FFD600] text-[#FFD600] rounded-full hover:bg-[#FFD600] hover:text-black transition-colors" data-testid="ord-soc-wa"><MessageCircle size={14} /></a>}
          {settings.youtube_url && <a href={settings.youtube_url} target="_blank" rel="noreferrer" className="w-8 h-8 flex items-center justify-center border border-[#FFD600] text-[#FFD600] rounded-full hover:bg-[#FFD600] hover:text-black transition-colors" data-testid="ord-soc-yt"><Youtube size={14} /></a>}
        </div>
      </div>
    </div>
  );
};

const PartRequests = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/part-requests").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.put(`/admin/part-requests/${id}/status`, { status }); load(); toast.success("U përditësua");
  };
  const remove = async (id) => {
    if (!window.confirm("A jeni i sigurt që doni ta fshini këtë kërkesë?")) return;
    try { await api.delete(`/admin/part-requests/${id}`); load(); toast.success("Kërkesa u fshi"); }
    catch { toast.error("Gabim gjatë fshirjes"); }
  };
  return (
    <div>
      <h1 className="font-display text-3xl uppercase mb-4">Kërkesat me foto</h1>
      <div className="space-y-3">
        {items.map(r => (
          <div key={r.id} data-testid={`req-row-${r.id}`} className="hg-card p-3 flex gap-3">
            {r.image_url ? <img src={mediaUrl(r.image_url)} alt="" data-testid={`req-img-${r.id}`} className="w-24 h-24 object-cover flex-shrink-0" />
              : <div className="w-24 h-24 bg-[#131E30] border border-[#1F2B44] flex items-center justify-center text-neutral-500"><Camera /></div>}
            <div className="flex-1 min-w-0">
              <div className="font-medium">{r.name} • {r.phone}</div>
              {r.part_number && <div className="text-xs text-neutral-400">Nr. pjesës: {r.part_number}</div>}
              {r.machine && <div className="text-xs text-neutral-400">Makineri: {r.machine}</div>}
              {r.message && <div className="text-xs text-neutral-300 mt-1">{r.message}</div>}
              <div className="text-[10px] text-neutral-500 mt-1">{new Date(r.created_at).toLocaleString("sq-AL")}</div>
            </div>
            <div className="flex flex-col gap-2 items-end">
              <select value={r.status} onChange={e => setStatus(r.id, e.target.value)} data-testid={`req-status-${r.id}`} className="bg-[#131E30] border border-[#1F2B44] px-2 py-1 text-xs">
                {REQ_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
              <button onClick={() => remove(r.id)} data-testid={`req-delete-${r.id}`}
                className="text-xs px-2 py-1 border border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors flex items-center gap-1">
                <Trash2 size={12} /> Fshij
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-neutral-400 py-16">Nuk ka kërkesa</div>}
      </div>
    </div>
  );
};

const ANGLES = ["Drejtë", "45°", "90°"];
const THREADS = ["BSP", "JIC", "Metric", "Flanxhë"];
const SIZES = ["1/8\"", "1/4\"", "3/8\"", "1/2\"", "5/8\"", "3/4\"", "1\"", "1 1/4\"", "1 1/2\"", "2\""];

const ImagePicker = ({ url, onChange }) => {
  const onFile = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    onChange(r.data.url);
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-20 h-20 bg-[#131E30] border border-[#1F2B44] overflow-hidden flex items-center justify-center">
        {url ? <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover" /> : <Camera size={20} className="text-neutral-600" />}
      </div>
      <label className="border border-[#1F2B44] px-3 py-2 text-xs uppercase cursor-pointer hover:bg-[#262626] inline-flex items-center gap-2">
        <Upload size={14} /> Ngarko foto
        <input type="file" accept="image/*" onChange={onFile} className="hidden" />
      </label>
      {url && <button type="button" onClick={() => onChange("")} className="text-xs text-red-400 hover:underline">Hiq</button>}
    </div>
  );
};

const CategoriesAdmin = () => {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const empty = { name: "", slug: "", parent_slug: "", image: "", order: 0 };
  const load = () => api.get("/categories").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const slugify = (s) => s.toLowerCase().replace(/[àáâãäå]/g,"a").replace(/[èéêë]/g,"e").replace(/[ìíîï]/g,"i").replace(/[òóôõö]/g,"o").replace(/[ùúûü]/g,"u").replace(/ç/g,"c").replace(/ë/g,"e").replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");

  const save = async () => {
    if (!edit.name) { toast.error("Emri është i detyrueshëm"); return; }
    const payload = { ...edit, slug: edit.slug || slugify(edit.name) };
    try {
      if (edit.id) await api.put(`/admin/categories/${edit.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success("U ruajt"); setEdit(null); load();
    } catch { toast.error("Gabim gjatë ruajtjes"); }
  };
  const del = async (id) => {
    if (!window.confirm("Fshi këtë kategori? Produktet e saj do të mbeten pa kategori.")) return;
    await api.delete(`/admin/categories/${id}`); load(); toast.success("U fshi");
  };
  const move = async (idx, dir) => {
    const target = items[idx];
    const isParent = !target.parent_slug;
    // Siblings: same-level (parents) or same-parent (children)
    const siblings = isParent
      ? items.filter(c => !c.parent_slug)
      : items.filter(c => c.parent_slug === target.parent_slug);
    const sibIdx = siblings.findIndex(x => x.id === target.id);
    const newSibIdx = sibIdx + dir;
    if (newSibIdx < 0 || newSibIdx >= siblings.length) return;
    // Swap within siblings
    [siblings[sibIdx], siblings[newSibIdx]] = [siblings[newSibIdx], siblings[sibIdx]];
    // Rebuild global ordered list: parents (in order) each followed by their children
    const parentsList = isParent ? siblings : items.filter(c => !c.parent_slug);
    const orderedArr = [];
    for (const p of parentsList) {
      orderedArr.push(p);
      const kids = (!isParent && p.slug === target.parent_slug)
        ? siblings
        : items.filter(c => c.parent_slug === p.slug);
      orderedArr.push(...kids);
    }
    // Append orphans at the end
    const orphansList = items.filter(c => c.parent_slug && !parentsList.some(p => p.slug === c.parent_slug));
    orderedArr.push(...orphansList);
    setItems(orderedArr);
    await api.put("/admin/categories/reorder", { ordered_ids: orderedArr.map(c => c.id) });
    toast.success("Renditja u ruajt");
  };

  // Group: parents with their children under each parent
  const parents = items.filter(c => !c.parent_slug);
  const childrenOf = (slug) => items.filter(c => c.parent_slug === slug);
  const orphans = items.filter(c => c.parent_slug && !parents.some(p => p.slug === c.parent_slug));

  const indexOf = (c) => items.findIndex(x => x.id === c.id);

  const CategoryRow = ({ c, isChild }) => {
    const idx = indexOf(c);
    return (
      <div data-testid={`cat-row-${c.slug}`}
        className={`flex items-center gap-3 p-3 ${isChild ? "pl-6 sm:pl-10 border-l-2 border-[#FFD600]/30 bg-black/40" : ""}`}>
        <div className="flex flex-col gap-0.5">
          <button onClick={() => move(idx, -1)} data-testid={`cat-up-${c.slug}`} className="w-6 h-6 flex items-center justify-center border border-[#1F2B44] hover:bg-[#262626]"><ArrowUp size={12} /></button>
          <button onClick={() => move(idx, 1)} data-testid={`cat-down-${c.slug}`} className="w-6 h-6 flex items-center justify-center border border-[#1F2B44] hover:bg-[#262626]"><ArrowDown size={12} /></button>
        </div>
        <div className={`w-14 h-14 bg-white overflow-hidden flex-shrink-0 ${isChild ? "border border-[#FFD600]/40" : ""}`}>
          {c.image && <img src={mediaUrl(c.image)} alt={c.name} className="w-full h-full object-contain" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate flex items-center gap-2">
            {isChild && <span className="text-[#FFD600] text-sm">↳</span>}
            {c.name}
          </div>
          <div className="text-xs text-neutral-500 font-mono truncate">{c.slug}</div>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setEdit({ ...c })} data-testid={`cat-edit-${c.slug}`} className="p-2 hover:bg-[#262626]"><Edit3 size={14} /></button>
          <button onClick={() => del(c.id)} data-testid={`cat-del-${c.slug}`} className="p-2 hover:bg-[#262626] text-red-400"><Trash2 size={14} /></button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-3xl uppercase">Kategoritë</h1>
        <button onClick={() => setEdit({ ...empty })} data-testid="admin-add-cat" className="hg-btn-primary px-4 py-2 uppercase text-xs inline-flex items-center gap-1"><Plus size={14} /> Shto kategori</button>
      </div>

      <div className="space-y-4">
        {parents.map(p => {
          const kids = childrenOf(p.slug);
          return (
            <div key={p.id} data-testid={`cat-group-${p.slug}`} className="hg-card overflow-hidden">
              <div className="bg-[#FFD600]/10 border-b-2 border-[#FFD600]/40 px-3 py-2 flex items-center gap-2">
                <div className="w-1.5 h-6 bg-[#FFD600]" />
                <div className="text-[10px] uppercase tracking-widest text-[#FFD600] font-bold">Kategoria kryesore</div>
                <span className="ml-auto text-[10px] text-neutral-400">
                  {kids.length > 0 ? `${kids.length} nënkategori` : "pa nënkategori"}
                </span>
              </div>
              <CategoryRow c={p} isChild={false} />
              {kids.length > 0 && (
                <div className="border-t border-[#1F2B44] bg-black/20">
                  {kids.map(k => <CategoryRow key={k.id} c={k} isChild />)}
                </div>
              )}
            </div>
          );
        })}
        {orphans.length > 0 && (
          <div className="hg-card overflow-hidden">
            <div className="bg-red-500/10 border-b-2 border-red-500/40 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold">Nënkategori pa prind (jetime)</div>
              <div className="text-[10px] text-neutral-500 mt-0.5">Prindi u fshi ose slug-u është i gabuar</div>
            </div>
            {orphans.map(o => <CategoryRow key={o.id} c={o} isChild />)}
          </div>
        )}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="hg-card max-w-lg w-full p-5" onClick={e => e.stopPropagation()}>
            <div className="font-display text-2xl uppercase mb-4">{edit.id ? "Ndrysho" : "Shto"} kategori</div>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase text-neutral-400">Fotografia</label>
                <div className="mt-2"><ImagePicker url={edit.image} onChange={(u) => setEdit({ ...edit, image: u })} /></div>
              </div>
              <div>
                <label className="text-xs uppercase text-neutral-400">Emri</label>
                <input value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value, slug: edit.id ? edit.slug : slugify(e.target.value) })} data-testid="cat-name" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-xs uppercase text-neutral-400">Slug (URL)</label>
                <input value={edit.slug} onChange={e => setEdit({ ...edit, slug: slugify(e.target.value) })} data-testid="cat-slug" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1 font-mono text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase text-neutral-400">Kategoria prind (opsionale)</label>
                <select value={edit.parent_slug || ""} onChange={e => setEdit({ ...edit, parent_slug: e.target.value })} data-testid="cat-parent" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1">
                  <option value="">— Kategori kryesore —</option>
                  {items.filter(c => !c.parent_slug && c.id !== edit.id).map(c => (
                    <option key={c.slug} value={c.slug}>{c.name}</option>
                  ))}
                </select>
                <div className="text-[10px] text-neutral-500 mt-1">Nëse zgjedh një kategori prind, kjo do të bëhet nënkategori dhe do të shfaqet brenda saj.</div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={save} data-testid="save-cat" className="hg-btn-primary flex-1 py-2 uppercase text-sm">Ruaj</button>
              <button onClick={() => setEdit(null)} className="border border-[#1F2B44] px-4 py-2 text-sm">Anulo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const HoseTypesTab = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);
  const empty = { code: "", pressure_bar: 0, standard: "", price_per_meter: 0, stock_qty: 0, image: "", category_slug: "" };
  const load = () => api.get("/configurator/data").then(r => setItems(r.data.hoses));
  useEffect(() => { load(); api.get("/categories").then(r => setCats(r.data)); }, []);

  const save = async () => {
    if (!edit.code) { toast.error("Kodi është i detyrueshëm"); return; }
    try {
      if (edit.id) await api.put(`/admin/hose-types/${edit.id}`, edit);
      else await api.post("/admin/hose-types", edit);
      toast.success("U ruajt"); setEdit(null); load();
    } catch { toast.error("Gabim gjatë ruajtjes"); }
  };
  const del = async (id) => {
    if (!window.confirm("Fshi këtë gyp?")) return;
    await api.delete(`/admin/hose-types/${id}`); load(); toast.success("U fshi");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-neutral-400">{items.length} gypa në konfigurator</div>
        <button onClick={() => setEdit({ ...empty })} data-testid="admin-add-hose" className="hg-btn-primary px-4 py-2 uppercase text-xs inline-flex items-center gap-1"><Plus size={14} /> Shto gyp</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map(h => (
          <div key={h.id} className="hg-card p-3" data-testid={`hose-row-${h.code}`}>
            <div className="aspect-square bg-[#131E30] overflow-hidden mb-3">
              {h.image && <img src={mediaUrl(h.image)} alt={h.code} className="w-full h-full object-cover" />}
            </div>
            <div className="font-display text-xl">{h.code}</div>
            <div className="text-xs text-neutral-400 mt-1">Presion: {h.pressure_bar} bar</div>
            <div className="text-xs text-neutral-400">Standardi: {h.standard}</div>
            <div className="text-[#FFD600] font-bold mt-2">{fmtEUR(h.price_per_meter)}/m</div>
            <div className="text-xs text-neutral-500 mt-1">Stok: {h.stock_qty}</div>
            <div className="flex gap-1 mt-3">
              <button onClick={() => setEdit({ ...h })} className="flex-1 border border-[#1F2B44] py-1.5 text-xs hover:bg-[#262626] inline-flex items-center justify-center gap-1"><Edit3 size={12} /> Ndrysho</button>
              <button onClick={() => del(h.id)} className="border border-[#1F2B44] py-1.5 px-2 text-xs text-red-400 hover:bg-[#262626]"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="hg-card max-w-lg w-full p-5" onClick={e => e.stopPropagation()}>
            <div className="font-display text-2xl uppercase mb-4">{edit.id ? "Ndrysho" : "Shto"} gyp</div>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase text-neutral-400">Fotografia</label>
                <div className="mt-2"><ImagePicker url={edit.image} onChange={(u) => setEdit({ ...edit, image: u })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase text-neutral-400">Kodi (p.sh. 2SN)</label>
                  <input value={edit.code} onChange={e => setEdit({ ...edit, code: e.target.value })} data-testid="hose-code" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400">Presioni (bar)</label>
                  <input type="number" value={edit.pressure_bar} onChange={e => setEdit({ ...edit, pressure_bar: +e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs uppercase text-neutral-400">Standardi</label>
                  <input value={edit.standard} onChange={e => setEdit({ ...edit, standard: e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400">Çmimi (€/m)</label>
                  <input type="number" step="0.01" value={edit.price_per_meter} onChange={e => setEdit({ ...edit, price_per_meter: +e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400">Stoku</label>
                  <input type="number" value={edit.stock_qty} onChange={e => setEdit({ ...edit, stock_qty: +e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs uppercase text-neutral-400">Lidhur me kategorinë (opsional)</label>
                  <select value={edit.category_slug || ""} onChange={e => setEdit({ ...edit, category_slug: e.target.value })} data-testid="hose-cat" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1">
                    <option value="">— pa lidhje —</option>
                    {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={save} data-testid="save-hose" className="hg-btn-primary flex-1 py-2 uppercase text-sm">Ruaj</button>
              <button onClick={() => setEdit(null)} className="border border-[#1F2B44] px-4 py-2 text-sm">Anulo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CouplingsTab = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);
  const [filter, setFilter] = useState({ size: "", thread: "" });
  const empty = { name: "", angle: "Drejtë", thread: "BSP", size: "1/2\"", price: 0, stock_qty: 0, image: "", category_slug: "" };
  const load = () => api.get("/configurator/data").then(r => setItems(r.data.couplings));
  useEffect(() => { load(); api.get("/categories").then(r => setCats(r.data)); }, []);

  const filtered = items.filter(c => (!filter.size || c.size === filter.size) && (!filter.thread || c.thread === filter.thread));

  const save = async () => {
    const payload = { ...edit, name: edit.name || `${edit.angle} ${edit.thread} ${edit.size}` };
    try {
      if (edit.id) await api.put(`/admin/couplings/${edit.id}`, payload);
      else await api.post("/admin/couplings", payload);
      toast.success("U ruajt"); setEdit(null); load();
    } catch { toast.error("Gabim gjatë ruajtjes"); }
  };
  const del = async (id) => {
    if (!window.confirm("Fshi këtë koplingë?")) return;
    await api.delete(`/admin/couplings/${id}`); load(); toast.success("U fshi");
  };


  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={filter.size} onChange={e => setFilter({ ...filter, size: e.target.value })} className="bg-[#131E30] border border-[#1F2B44] px-3 py-2 text-sm">
          <option value="">Të gjitha madhësitë</option>
          {SIZES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filter.thread} onChange={e => setFilter({ ...filter, thread: e.target.value })} className="bg-[#131E30] border border-[#1F2B44] px-3 py-2 text-sm">
          <option value="">Të gjitha filetot</option>
          {THREADS.map(s => <option key={s}>{s}</option>)}
        </select>
        <div className="text-sm text-neutral-400 ml-auto">{filtered.length} koplingë</div>
        <button onClick={() => setEdit({ ...empty })} data-testid="admin-add-coupling" className="hg-btn-primary px-4 py-2 uppercase text-xs inline-flex items-center gap-1"><Plus size={14} /> Shto koplingë</button>
      </div>
      <div className="hg-card overflow-x-auto -webkit-overflow-scrolling-touch">
        <table className="w-max min-w-full text-sm">
          <thead className="text-xs uppercase text-neutral-400 border-b border-[#1F2B44]">
            <tr><th className="p-3 text-left whitespace-nowrap">Foto</th><th className="p-3 text-left whitespace-nowrap">Emri</th><th className="p-3 text-left whitespace-nowrap">Këndi</th><th className="p-3 text-left whitespace-nowrap">Fileta</th><th className="p-3 text-left whitespace-nowrap">Madhësia</th><th className="p-3 text-right whitespace-nowrap">Çmimi</th><th className="p-3 text-right whitespace-nowrap">Stok</th><th className="whitespace-nowrap"></th></tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} className="border-b border-[#1F2B44]" data-testid={`coupling-row-${c.id}`}>
                <td className="p-2 whitespace-nowrap"><div className="w-12 h-12 bg-[#131E30] overflow-hidden">{c.image && <img src={mediaUrl(c.image)} alt="" className="w-full h-full object-cover" />}</div></td>
                <td className="p-3 whitespace-nowrap">{c.name}</td>
                <td className="p-3 text-neutral-400 whitespace-nowrap">{c.angle}</td>
                <td className="p-3 text-neutral-400 whitespace-nowrap">{c.thread}</td>
                <td className="p-3 text-neutral-400 whitespace-nowrap">{c.size}</td>
                <td className="p-3 text-right text-[#FFD600] whitespace-nowrap">{fmtEUR(c.price)}</td>
                <td className="p-3 text-right whitespace-nowrap">{c.stock_qty}</td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <button onClick={() => setEdit({ ...c })} className="p-1.5 hover:bg-[#262626]"><Edit3 size={14} /></button>
                  <button onClick={() => del(c.id)} className="p-1.5 hover:bg-[#262626] text-red-400"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden text-[10px] text-neutral-500 mt-1 text-center">← Rrëshqit majtas/djathtas për të parë të gjitha kolonat →</div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="hg-card max-w-lg w-full p-5" onClick={e => e.stopPropagation()}>
            <div className="font-display text-2xl uppercase mb-4">{edit.id ? "Ndrysho" : "Shto"} koplingë</div>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase text-neutral-400">Fotografia</label>
                <div className="mt-2"><ImagePicker url={edit.image} onChange={(u) => setEdit({ ...edit, image: u })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs uppercase text-neutral-400">Emri (opsional — auto)</label>
                  <input value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} placeholder={`${edit.angle} ${edit.thread} ${edit.size}`} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400">Këndi</label>
                  <select value={edit.angle} onChange={e => setEdit({ ...edit, angle: e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1">
                    {ANGLES.map(a => <option key={a}>{a}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400">Fileta</label>
                  <select value={edit.thread} onChange={e => setEdit({ ...edit, thread: e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1">
                    {THREADS.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400">Madhësia</label>
                  <select value={edit.size} onChange={e => setEdit({ ...edit, size: e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1">
                    {SIZES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400">Çmimi (€)</label>
                  <input type="number" step="0.01" value={edit.price} onChange={e => setEdit({ ...edit, price: +e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400">Stoku</label>
                  <input type="number" value={edit.stock_qty} onChange={e => setEdit({ ...edit, stock_qty: +e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs uppercase text-neutral-400">Lidhur me kategorinë (opsional)</label>
                  <select value={edit.category_slug || ""} onChange={e => setEdit({ ...edit, category_slug: e.target.value })} data-testid="coupling-cat" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1">
                    <option value="">— pa lidhje —</option>
                    {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={save} data-testid="save-coupling" className="hg-btn-primary flex-1 py-2 uppercase text-sm">Ruaj</button>
              <button onClick={() => setEdit(null)} className="border border-[#1F2B44] px-4 py-2 text-sm">Anulo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FerrulesTab = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);
  const [filter, setFilter] = useState({ size: "", hose_type: "" });
  const [hoseCodes, setHoseCodes] = useState([]);
  const empty = { code: "", size: "1/2\"", hose_type: "", price: 0, stock_qty: 0, image: "", category_slug: "" };
  const load = () => api.get("/configurator/data").then(r => { setItems(r.data.ferrules || []); setHoseCodes(r.data.hoses.map(h => h.code)); });
  useEffect(() => { load(); api.get("/categories").then(r => setCats(r.data)); }, []);

  const filtered = items.filter(f => (!filter.size || f.size === filter.size) && (!filter.hose_type || f.hose_type === filter.hose_type));

  const save = async () => {
    if (!edit.code) { toast.error("Kodi është i detyrueshëm"); return; }
    try {
      if (edit.id) await api.put(`/admin/ferrules/${edit.id}`, edit);
      else await api.post("/admin/ferrules", edit);
      toast.success("U ruajt"); setEdit(null); load();
    } catch { toast.error("Gabim gjatë ruajtjes"); }
  };
  const del = async (id) => {
    if (!window.confirm("Fshi këtë hylsë?")) return;
    await api.delete(`/admin/ferrules/${id}`); load(); toast.success("U fshi");
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={filter.size} onChange={e => setFilter({ ...filter, size: e.target.value })} className="bg-[#131E30] border border-[#1F2B44] px-3 py-2 text-sm">
          <option value="">Të gjitha madhësitë</option>
          {SIZES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filter.hose_type} onChange={e => setFilter({ ...filter, hose_type: e.target.value })} className="bg-[#131E30] border border-[#1F2B44] px-3 py-2 text-sm">
          <option value="">Të gjithë gypat</option>
          {hoseCodes.map(c => <option key={c}>{c}</option>)}
        </select>
        <div className="text-sm text-neutral-400 ml-auto">{filtered.length} hylsa</div>
        <button onClick={() => setEdit({ ...empty })} data-testid="admin-add-ferrule" className="hg-btn-primary px-4 py-2 uppercase text-xs inline-flex items-center gap-1"><Plus size={14} /> Shto hylsë</button>
      </div>
      <div className="hg-card overflow-x-auto -webkit-overflow-scrolling-touch">
        <table className="w-max min-w-full text-sm">
          <thead className="text-xs uppercase text-neutral-400 border-b border-[#1F2B44]">
            <tr><th className="p-3 text-left whitespace-nowrap">Foto</th><th className="p-3 text-left whitespace-nowrap">Kodi</th><th className="p-3 text-left whitespace-nowrap">Madhësia</th><th className="p-3 text-left whitespace-nowrap">Për gyp</th><th className="p-3 text-right whitespace-nowrap">Çmimi</th><th className="p-3 text-right whitespace-nowrap">Stok</th><th className="whitespace-nowrap"></th></tr>
          </thead>
          <tbody>
            {filtered.map(f => (
              <tr key={f.id} className="border-b border-[#1F2B44]" data-testid={`ferrule-row-${f.id}`}>
                <td className="p-2 whitespace-nowrap"><div className="w-12 h-12 bg-[#131E30] overflow-hidden">{f.image && <img src={mediaUrl(f.image)} alt="" className="w-full h-full object-cover" />}</div></td>
                <td className="p-3 whitespace-nowrap">{f.code}</td>
                <td className="p-3 text-neutral-400 whitespace-nowrap">{f.size}</td>
                <td className="p-3 text-neutral-400 whitespace-nowrap">{f.hose_type || "—"}</td>
                <td className="p-3 text-right text-[#FFD600] whitespace-nowrap">{fmtEUR(f.price)}</td>
                <td className="p-3 text-right whitespace-nowrap">{f.stock_qty}</td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <button onClick={() => setEdit({ ...f })} className="p-1.5 hover:bg-[#262626]"><Edit3 size={14} /></button>
                  <button onClick={() => del(f.id)} className="p-1.5 hover:bg-[#262626] text-red-400"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden text-[10px] text-neutral-500 mt-1 text-center">← Rrëshqit majtas/djathtas për të parë të gjitha kolonat →</div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="hg-card max-w-lg w-full p-5" onClick={e => e.stopPropagation()}>
            <div className="font-display text-2xl uppercase mb-4">{edit.id ? "Ndrysho" : "Shto"} hylsë</div>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase text-neutral-400">Fotografia</label>
                <div className="mt-2"><ImagePicker url={edit.image} onChange={(u) => setEdit({ ...edit, image: u })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs uppercase text-neutral-400">Kodi (p.sh. H-2SN-12)</label>
                  <input value={edit.code} onChange={e => setEdit({ ...edit, code: e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400">Madhësia</label>
                  <select value={edit.size} onChange={e => setEdit({ ...edit, size: e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1">
                    {SIZES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400">Për gyp (opsional)</label>
                  <select value={edit.hose_type} onChange={e => setEdit({ ...edit, hose_type: e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1">
                    <option value="">— të gjithë —</option>
                    {hoseCodes.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400">Çmimi (€)</label>
                  <input type="number" step="0.01" value={edit.price} onChange={e => setEdit({ ...edit, price: +e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400">Stoku</label>
                  <input type="number" value={edit.stock_qty} onChange={e => setEdit({ ...edit, stock_qty: +e.target.value })} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs uppercase text-neutral-400">Lidhur me kategorinë (opsional)</label>
                  <select value={edit.category_slug || ""} onChange={e => setEdit({ ...edit, category_slug: e.target.value })} data-testid="ferrule-cat" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1">
                    <option value="">— pa lidhje —</option>
                    {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={save} data-testid="save-ferrule" className="hg-btn-primary flex-1 py-2 uppercase text-sm">Ruaj</button>
              <button onClick={() => setEdit(null)} className="border border-[#1F2B44] px-4 py-2 text-sm">Anulo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Customers = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/admin/customers").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = items.filter(u => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s) || (u.phone || "").includes(s);
  });

  const toggleBlock = async (u) => {
    const willBlock = !u.blocked;
    if (willBlock && !window.confirm(`Blloko klientin "${u.name}"? Nuk do të mund të kyçet më.`)) return;
    try { await api.put(`/admin/customers/${u.id}`, { blocked: willBlock }); load(); toast.success(willBlock ? "Klienti u bllokua" : "Klienti u zhbllokua"); }
    catch { toast.error("Gabim"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Fshi klientin "${u.name}" përgjithmonë? Ky veprim nuk mund të kthehet.`)) return;
    try { await api.delete(`/admin/customers/${u.id}`); load(); toast.success("Klienti u fshi"); }
    catch { toast.error("Gabim gjatë fshirjes"); }
  };
  const save = async () => {
    try {
      await api.put(`/admin/customers/${edit.id}`, {
        name: edit.name, phone: edit.phone, address: edit.address, city: edit.city,
        company_name: edit.company_name, street: edit.street, house_number: edit.house_number,
        postal_code: edit.postal_code, country: edit.country, preferred_contact: edit.preferred_contact,
      });
      setEdit(null); load(); toast.success("Të dhënat u ruajtën");
    } catch { toast.error("Gabim"); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-cyan-500/15 border border-cyan-500/40 flex items-center justify-center">
          <Users size={20} className="text-cyan-400" />
        </div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl uppercase">Klientët</h1>
          <div className="text-xs text-neutral-400">{items.length} klientë të regjistruar</div>
        </div>
      </div>

      <div className="mb-3 relative">
        <SearchIcon size={16} className="absolute left-3 top-3 text-neutral-500" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Kërko me emër, email ose telefon..."
          data-testid="customers-search"
          className="w-full bg-[#131E30] border border-[#1F2B44] pl-10 pr-3 py-2.5 text-sm focus:border-[#FFD600] outline-none" />
      </div>

      {filtered.length === 0 ? (
        <div className="hg-card p-8 text-center text-neutral-400" data-testid="customers-empty">
          {items.length === 0 ? "Ende nuk ka klientë të regjistruar" : "Asnjë klient nuk përputhet me kërkimin"}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(u => (
            <div key={u.id} data-testid={`customer-row-${u.id}`}
              className={`hg-card p-3 sm:p-4 ${u.blocked ? "border-l-4 border-red-500/70 opacity-70" : ""}`}>
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-[#FFD600]/15 border border-[#FFD600]/40 flex items-center justify-center font-display text-lg text-[#FFD600] flex-shrink-0">
                  {(u.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    {u.name}
                    {u.blocked && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-red-500/20 text-red-400 border border-red-500/40">Bllokuar</span>}
                  </div>
                  <div className="text-xs text-neutral-400 truncate">{u.email}</div>
                  {u.phone && <div className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5"><PhoneIcon size={11} /> {u.phone}</div>}
                  {(u.address || u.city) && <div className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5"><MapPin size={11} /> {[u.address, u.city].filter(Boolean).join(", ")}</div>}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#1F2B44]">
                <div className="flex-1 flex gap-3 text-[11px] text-neutral-400">
                  <span><b className="text-white">{u.orders_count}</b> porosi</span>
                  <span><b className="text-[#FFD600]">{fmtEUR(u.total_spent)}</b> total</span>
                  <span className="hidden sm:inline">Regjistrua: {u.created_at ? new Date(u.created_at).toLocaleDateString("sq-AL") : "—"}</span>
                </div>
                <button onClick={() => setEdit({ ...u })} data-testid={`customer-edit-${u.id}`}
                  className="p-2 border border-[#1F2B44] hover:border-[#FFD600] hover:text-[#FFD600]" title="Ndrysho">
                  <Edit3 size={14} />
                </button>
                <button onClick={() => toggleBlock(u)} data-testid={`customer-block-${u.id}`}
                  className={`p-2 border ${u.blocked ? "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500 hover:text-white" : "border-orange-500/40 text-orange-400 hover:bg-orange-500 hover:text-white"}`}
                  title={u.blocked ? "Zhblloko" : "Blloko"}>
                  {u.blocked ? <Check size={14} /> : <Ban size={14} />}
                </button>
                <button onClick={() => remove(u)} data-testid={`customer-del-${u.id}`}
                  className="p-2 border border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white" title="Fshi">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="hg-card max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-2xl uppercase">Ndrysho klientin</div>
              <button onClick={() => setEdit(null)}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              {[
                { key: "name", label: "Emri dhe mbiemri", icon: UserIcon },
                { key: "company_name", label: "Emri i firmës", icon: SettingsIcon },
                { key: "phone", label: "Telefoni", icon: PhoneIcon },
                { key: "street", label: "Rruga", icon: MapPin },
                { key: "house_number", label: "Numri i shtëpisë", icon: MapPin },
                { key: "postal_code", label: "Kodi postar", icon: MapPin },
                { key: "city", label: "Qyteti", icon: MapPin },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-[10px] uppercase tracking-widest text-neutral-400 flex items-center gap-1.5 mb-1"><f.icon size={10} /> {f.label}</label>
                  <input value={edit[f.key] || ""} onChange={e => setEdit({ ...edit, [f.key]: e.target.value })}
                    data-testid={`customer-input-${f.key}`}
                    className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 focus:border-[#FFD600] outline-none text-sm" />
                </div>
              ))}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1 block">Shteti</label>
                <select value={edit.country || "Kosovë"} onChange={e => setEdit({ ...edit, country: e.target.value })}
                  data-testid="customer-input-country"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 focus:border-[#FFD600] outline-none text-sm">
                  {["Kosovë","Shqipëri","Maqedoni e Veriut","Mal i Zi"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1 block">Mënyra e kontaktit</label>
                <select value={edit.preferred_contact || "Telefon"} onChange={e => setEdit({ ...edit, preferred_contact: e.target.value })}
                  data-testid="customer-input-contact"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 focus:border-[#FFD600] outline-none text-sm">
                  {["Telefon","WhatsApp","Viber","Email"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1 block">Email</label>
                <div className="px-3 py-2 bg-black/40 border border-[#1F2B44] text-sm text-neutral-500">{edit.email}</div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={save} data-testid="customer-save" className="hg-btn-primary flex-1 py-2.5 uppercase text-xs inline-flex items-center justify-center gap-1"><Save size={14} /> Ruaj</button>
              <button onClick={() => setEdit(null)} className="border border-[#1F2B44] px-4 py-2.5 text-xs">Anulo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const KontaktField = ({ fkey, label, icon, placeholder, multiline, hint, s, editing, draft, savingKey, startEdit, cancelEdit, saveField, setDraft }) => {
  const isEditing = editing[fkey];
  const value = s[fkey] || "";
  return (
    <div className="hg-card p-4" data-testid={`kontakt-field-${fkey}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#FFD600] font-bold">
          {icon && <span>{icon}</span>}{label}
        </div>
        {!isEditing ? (
          <button onClick={() => startEdit(fkey)} data-testid={`edit-${fkey}`}
            className="text-xs uppercase tracking-wider px-3 py-1.5 border border-[#1F2B44] hover:border-[#FFD600] hover:text-[#FFD600] flex items-center gap-1">
            <Edit3 size={12} /> Ndrysho
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => cancelEdit(fkey)} className="text-xs uppercase tracking-wider px-3 py-1.5 border border-[#1F2B44] hover:border-red-500 hover:text-red-400">Anulo</button>
            <button onClick={() => saveField(fkey)} disabled={savingKey === fkey} data-testid={`save-${fkey}`}
              className="hg-btn-primary text-xs uppercase tracking-wider px-3 py-1.5 flex items-center gap-1 disabled:opacity-50">
              <Check size={12} /> {savingKey === fkey ? "..." : "Ruaj"}
            </button>
          </div>
        )}
      </div>
      {!isEditing ? (
        <div className="text-sm text-neutral-200 whitespace-pre-line break-words min-h-[24px]" data-testid={`value-${fkey}`}>
          {value || <span className="text-neutral-500 italic">— pa vlerë —</span>}
        </div>
      ) : multiline ? (
        <textarea value={draft[fkey] ?? ""} onChange={e => setDraft(prev => ({ ...prev, [fkey]: e.target.value }))}
          placeholder={placeholder} rows={3} data-testid={`input-${fkey}`}
          className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 text-sm focus:border-[#FFD600] outline-none resize-y" />
      ) : (
        <input value={draft[fkey] ?? ""} onChange={e => setDraft(prev => ({ ...prev, [fkey]: e.target.value }))}
          placeholder={placeholder} data-testid={`input-${fkey}`}
          className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 text-sm focus:border-[#FFD600] outline-none" />
      )}
      {hint && <div className="text-[11px] text-neutral-500 mt-2">{hint}</div>}
    </div>
  );
};

const KontaktAdmin = () => {
  const [s, setS] = useState(null);
  const [editing, setEditing] = useState({}); // field -> true if in edit mode
  const [draft, setDraft] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    api.get("/settings").then(r => { setS(r.data); setDraft(r.data); }).catch(() => toast.error("Gabim gjatë ngarkimit"));
  }, []);

  const startEdit = (key) => { setEditing(prev => ({ ...prev, [key]: true })); setDraft(prev => ({ ...prev, [key]: s?.[key] || "" })); };
  const cancelEdit = (key) => { setEditing(prev => ({ ...prev, [key]: false })); setDraft(prev => ({ ...prev, [key]: s?.[key] || "" })); };
  const saveField = async (key) => {
    setSavingKey(key);
    try {
      await api.put("/admin/settings", { [key]: draft[key] || "" });
      setS(prev => ({ ...prev, [key]: draft[key] || "" }));
      setEditing(prev => ({ ...prev, [key]: false }));
      toast.success("U ruajt");
    } catch { toast.error("Gabim gjatë ruajtjes"); }
    setSavingKey(null);
  };

  if (!s) return <div className="text-neutral-400 text-sm">Duke ngarkuar...</div>;

  const fieldProps = { s, editing, draft, savingKey, startEdit, cancelEdit, saveField, setDraft };

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl uppercase mb-2">Kontakt</h1>
      <p className="text-sm text-neutral-400 mb-5">Të dhënat e kontaktit shfaqen automatikisht në faqen publike <code className="text-[#FFD600]">/kontakt</code> dhe në Footer.</p>

      <div className="space-y-3 mb-6">
        <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold pl-1">Informacione bazë</div>
        <KontaktField {...fieldProps} fkey="address" label="Adresa" icon="📍" placeholder="Rruga e Testit 1&#10;10000 Prishtinë&#10;Kosovë" multiline />
        <KontaktField {...fieldProps} fkey="contact_phone" label="Telefoni" icon="📞" placeholder="+383 44 123 456" />
        <KontaktField {...fieldProps} fkey="whatsapp_number" label="WhatsApp" icon="💬" placeholder="+383 44 123 456" hint="Përdoret edhe për butonin flotues WhatsApp." />
        <KontaktField {...fieldProps} fkey="viber_number" label="Viber" icon="🟣" placeholder="+383 44 123 456" />
        <KontaktField {...fieldProps} fkey="email" label="Email" icon="📧" placeholder="info@hidrogv.com" />
        <KontaktField {...fieldProps} fkey="working_hours" label="Orari i punës" icon="🕐" placeholder="E Hënë – E Premte: 08:00 – 17:00&#10;E Shtunë: 08:00 – 13:00" multiline />
      </div>

      <div className="space-y-3 mb-6">
        <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold pl-1">Lokacioni</div>
        <KontaktField {...fieldProps} fkey="google_maps_url" label="Google Maps — Link" icon="🗺️" placeholder="https://maps.google.com/?q=..." hint="Linku që hapet kur klikohet 'Hap në Google Maps'." />
        <KontaktField {...fieldProps} fkey="google_maps_embed" label="Google Maps — Embed URL (iframe src)" icon="🗺️" placeholder="https://www.google.com/maps/embed?pb=..." hint={'Merret nga Google Maps → Share → Embed a map → kopjoni vetëm URL-në brenda src="...".'} />
      </div>

      <div className="space-y-3 mb-6">
        <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold pl-1">Çmimet e transportit sipas shtetit (€)</div>
        <div className="hg-card p-4" data-testid="shipping-fees-editor">
          <div className="grid gap-3">
            {["Kosovë","Shqipëri","Maqedoni e Veriut","Mal i Zi"].map(country => {
              const fees = s.shipping_fees || {};
              const enabled = Object.prototype.hasOwnProperty.call(fees, country);
              const isEditing = editing[`_shipping_${country}`];
              const toggle = async () => {
                const newFees = { ...fees };
                if (enabled) { delete newFees[country]; }
                else { newFees[country] = 5; }
                try {
                  await api.put("/admin/settings", { shipping_fees: newFees });
                  setS(prev => ({ ...prev, shipping_fees: newFees }));
                  toast.success(enabled ? `${country} u fik` : `${country} u aktivizua`);
                } catch { toast.error("Gabim"); }
              };
              return (
                <div key={country} className={`flex items-center gap-2 flex-wrap ${!enabled ? "opacity-60" : ""}`}>
                  <div className="flex-1 text-sm min-w-0">
                    {country}
                    {!enabled && <span className="ml-2 text-[10px] uppercase tracking-widest text-red-400">— fikur</span>}
                  </div>
                  {enabled && isEditing ? (
                    <>
                      <input type="number" step="0.01" defaultValue={fees[country] ?? 0}
                        data-testid={`shipping-input-${country.replace(/\s+/g, '-')}`}
                        onChange={e => setDraft(prev => ({ ...prev, [`_shipping_${country}`]: e.target.value }))}
                        className="w-24 bg-[#131E30] border border-[#FFD600] px-3 py-1.5 text-sm focus:outline-none" />
                      <span className="text-xs text-neutral-400">€</span>
                      <button onClick={async () => {
                        const newFees = { ...fees, [country]: parseFloat(draft[`_shipping_${country}`] ?? fees[country] ?? 0) || 0 };
                        try {
                          await api.put("/admin/settings", { shipping_fees: newFees });
                          setS(prev => ({ ...prev, shipping_fees: newFees }));
                          setEditing(prev => ({ ...prev, [`_shipping_${country}`]: false }));
                          toast.success("U ruajt");
                        } catch { toast.error("Gabim"); }
                      }} data-testid={`shipping-save-${country.replace(/\s+/g, '-')}`}
                        className="hg-btn-primary text-xs uppercase tracking-wider px-3 py-1.5">Ruaj</button>
                    </>
                  ) : enabled ? (
                    <>
                      <div className="w-20 text-right font-mono font-bold text-[#FFD600]">{(fees[country] ?? 0).toFixed(2)} €</div>
                      <button onClick={() => setEditing(prev => ({ ...prev, [`_shipping_${country}`]: true }))}
                        data-testid={`shipping-edit-${country.replace(/\s+/g, '-')}`}
                        className="text-xs uppercase tracking-wider px-2 py-1.5 border border-[#1F2B44] hover:border-[#FFD600] hover:text-[#FFD600] flex items-center gap-1">
                        <Edit3 size={12} /> Ndrysho
                      </button>
                      <button onClick={toggle}
                        data-testid={`shipping-disable-${country.replace(/\s+/g, '-')}`}
                        className="text-xs uppercase tracking-wider px-2 py-1.5 border border-[#1F2B44] hover:border-red-500 hover:text-red-400 flex items-center gap-1">
                        <Ban size={12} /> Fike
                      </button>
                    </>
                  ) : (
                    <button onClick={toggle}
                      data-testid={`shipping-enable-${country.replace(/\s+/g, '-')}`}
                      className="text-xs uppercase tracking-wider px-3 py-1.5 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-black flex items-center gap-1">
                      <Check size={12} /> Aktivizo
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-[11px] text-neutral-500 mt-3 pt-3 border-t border-[#1F2B44]">
            Këto çmime aplikohen automatikisht te checkout-i sipas shtetit që zgjedh klienti.
          </div>
        </div>
      </div>

      <div className="space-y-3 mb-6">
        <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold pl-1">Rrjete sociale</div>
        <KontaktField {...fieldProps} fkey="facebook_url" label="Facebook" icon="📘" placeholder="https://facebook.com/hidrogv" />
        <KontaktField {...fieldProps} fkey="instagram_url" label="Instagram" icon="📸" placeholder="https://instagram.com/hidrogv" />
        <KontaktField {...fieldProps} fkey="tiktok_url" label="TikTok" icon="🎵" placeholder="https://tiktok.com/@hidrogv" />
        <KontaktField {...fieldProps} fkey="youtube_url" label="YouTube" icon="▶️" placeholder="https://youtube.com/@hidrogv" />
      </div>
    </div>
  );
};

const SizeGroupsEditor = ({ title, desc, groups, onChange, testidPrefix }) => {
  const entries = Object.entries(groups || {});
  const [newGroupName, setNewGroupName] = useState("");
  const updateGroupValues = (label, text) => {
    const values = text.split("\n").map(x => x.trim()).filter(Boolean);
    onChange({ ...groups, [label]: values });
  };
  const renameGroup = (oldLabel, newLabel) => {
    if (!newLabel.trim() || newLabel === oldLabel) return;
    const next = {};
    for (const [k, v] of entries) next[k === oldLabel ? newLabel : k] = v;
    onChange(next);
  };
  const removeGroup = (label) => {
    if (!window.confirm(`Fshij grupin "${label}"?`)) return;
    const next = { ...groups };
    delete next[label];
    onChange(next);
  };
  const addGroup = () => {
    const name = newGroupName.trim();
    if (!name || groups[name]) return;
    onChange({ ...groups, [name]: [] });
    setNewGroupName("");
  };
  return (
    <div className="border border-[#1F2B44] bg-[#0B1220]/40 p-4 space-y-3">
      <div>
        <div className="text-sm text-[#FFD600] uppercase tracking-wider font-bold">{title}</div>
        <div className="text-xs text-neutral-400 mt-1">{desc}</div>
      </div>
      <div className="space-y-3">
        {entries.length === 0 && <div className="text-xs text-neutral-500 italic">— asnjë grup —</div>}
        {entries.map(([label, values]) => (
          <div key={label} className="border border-[#1F2B44] p-3">
            <div className="flex items-center gap-2 mb-2">
              <input defaultValue={label} onBlur={e => renameGroup(label, e.target.value)}
                data-testid={`${testidPrefix}-group-name-${label}`}
                className="flex-1 bg-[#131E30] border border-[#1F2B44] px-2 py-1 text-sm font-bold uppercase" />
              <button onClick={() => removeGroup(label)} data-testid={`${testidPrefix}-group-del-${label}`}
                className="text-xs px-2 py-1 border border-red-500/50 text-red-400 hover:bg-red-500/10">Fshi</button>
            </div>
            <textarea value={(values || []).join("\n")} onChange={e => updateGroupValues(label, e.target.value)}
              data-testid={`${testidPrefix}-group-values-${label}`}
              rows={Math.min(10, Math.max(3, (values || []).length + 1))}
              placeholder="Një vlerë për rresht, p.sh. AD1"
              className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-1 text-sm font-mono" />
            <div className="text-[10px] text-neutral-500 mt-1">{(values || []).length} vlera • Një vlerë për rresht</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
          placeholder="Emri i grupit të ri (p.sh. HYLSA XL)"
          data-testid={`${testidPrefix}-new-group-name`}
          className="flex-1 bg-[#131E30] border border-[#1F2B44] px-3 py-2 text-sm" />
        <button onClick={addGroup} data-testid={`${testidPrefix}-add-group`} disabled={!newGroupName.trim()}
          className="hg-btn-primary text-xs uppercase px-3 py-2 disabled:opacity-40">+ Shto grup</button>
      </div>
    </div>
  );
};

const Cilesimet = () => {
  const [s, setS] = useState({ whatsapp_number: "", contact_phone: "", hose_category_slug: "", ferrule_category_slug: "", coupling_category_slug: "" });
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/settings"), api.get("/categories")])
      .then(([sr, cr]) => { setS(prev => ({ ...prev, ...sr.data })); setCats(cr.data); setReady(true); })
      .catch(() => setReady(true));
  }, []);
  const save = async () => {
    setLoading(true);
    try {
      await api.put("/admin/settings", {
        whatsapp_number: s.whatsapp_number, contact_phone: s.contact_phone,
        hose_category_slug: s.hose_category_slug, ferrule_category_slug: s.ferrule_category_slug,
        coupling_category_slug: s.coupling_category_slug,
        diameters: s.diameters || {},
        size_codes: s.size_codes || {},
      });
      toast.success("Cilësimet u ruajtën");
    } catch { toast.error("Gabim gjatë ruajtjes"); }
    setLoading(false);
  };
  const catSelect = (key, label, testid) => (
    <div>
      <label className="text-xs uppercase text-neutral-400 tracking-wider">{label}</label>
      <select disabled={!ready} value={s[key] || ""} onChange={e => setS({ ...s, [key]: e.target.value })} data-testid={testid} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-3 mt-2 disabled:opacity-40">
        <option value="">— përdor koleksionin e vjetër —</option>
        {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
      </select>
    </div>
  );
  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-3xl uppercase mb-4">Cilësimet</h1>
      <div className="hg-card p-5 space-y-4">
        {!ready && <div className="text-sm text-neutral-400">Duke ngarkuar...</div>}
        <div>
          <label className="text-xs uppercase text-neutral-400 tracking-wider">Numri i WhatsApp</label>
          <input disabled={!ready} value={s.whatsapp_number || ""} onChange={e => setS({ ...s, whatsapp_number: e.target.value })} data-testid="set-whatsapp"
            placeholder="+38343800252" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-3 mt-2 font-mono disabled:opacity-40" />
        </div>
        <div>
          <label className="text-xs uppercase text-neutral-400 tracking-wider">Numri i kontaktit</label>
          <input disabled={!ready} value={s.contact_phone || ""} onChange={e => setS({ ...s, contact_phone: e.target.value })} data-testid="set-phone"
            placeholder="+38343800252" className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-3 mt-2 font-mono disabled:opacity-40" />
        </div>
        <div className="border border-[#FFD600]/30 bg-[#FFD600]/5 p-4 mt-2 space-y-3">
          <div className="text-sm text-[#FFD600] uppercase tracking-wider font-bold">⚙️ Konfigurator — lidh me produktet</div>
          <div className="text-xs text-neutral-400">Zgjidh kategoritë e produkteve që përdoren në konfigurator. Nëse i lë bosh, do të përdoret koleksioni i vjetër (Gyp/Hylsa/Kopllinga në tab-in Konfigurator).</div>
          {catSelect("hose_category_slug", "Kategoria e gypave (Hapi 1)", "set-hose-cat")}
          {catSelect("ferrule_category_slug", "Kategoria e hylsave (Hapi 2)", "set-ferrule-cat")}
          {catSelect("coupling_category_slug", "Kategoria e kopllingave (Hapi 3-4)", "set-coupling-cat")}
          <div className="text-[11px] text-neutral-500">Produktet e këtyre kategorive duhet të kenë <strong>diametrin</strong> të vendosur (fusha Diametri). Çmimi, stoku dhe foto lexohen direkt nga produkti.</div>
        </div>
        <SizeGroupsEditor
          title="Diametrat"
          desc="Grupet dhe vlerat që shfaqen te formulari i produktit — fusha Diametri."
          groups={s.diameters || {}}
          onChange={(next) => setS({ ...s, diameters: next })}
          testidPrefix="diam"
        />
        <SizeGroupsEditor
          title="Madhësi / Kode Modeli"
          desc="Grupet dhe kodet që shfaqen te formulari i produktit — fusha Madhësia (Kodi/Modeli)."
          groups={s.size_codes || {}}
          onChange={(next) => setS({ ...s, size_codes: next })}
          testidPrefix="scode"
        />
        <button disabled={loading || !ready} onClick={save} data-testid="set-save" className="hg-btn-primary w-full py-3 uppercase text-sm disabled:opacity-40">
          {loading ? "Duke ruajtur..." : "Ruaj cilësimet"}
        </button>
      </div>
    </div>
  );
};

const TekField = ({ fkey, label, placeholder, multiline, hint, s, editing, draft, savingKey, startEdit, cancelEdit, saveField, setDraft }) => {
  const isEditing = editing[fkey];
  const value = s[fkey] || "";
  return (
    <div className="hg-card p-4" data-testid={`tk-field-${fkey}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs uppercase tracking-widest text-[#FFD600] font-bold">{label}</div>
        {!isEditing ? (
          <button onClick={() => startEdit(fkey)} data-testid={`tk-edit-${fkey}`}
            className="text-xs uppercase tracking-wider px-3 py-1.5 border border-[#1F2B44] hover:border-[#FFD600] hover:text-[#FFD600] flex items-center gap-1">
            <Edit3 size={12} /> Ndrysho
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => cancelEdit(fkey)} className="text-xs uppercase tracking-wider px-3 py-1.5 border border-[#1F2B44] hover:border-red-500 hover:text-red-400">Anulo</button>
            <button onClick={() => saveField(fkey)} disabled={savingKey === fkey} data-testid={`tk-save-${fkey}`}
              className="hg-btn-primary text-xs uppercase tracking-wider px-3 py-1.5 flex items-center gap-1 disabled:opacity-50">
              <Check size={12} /> {savingKey === fkey ? "..." : "Ruaj"}
            </button>
          </div>
        )}
      </div>
      {!isEditing ? (
        <div className="text-sm text-neutral-200 whitespace-pre-line break-words min-h-[24px]" data-testid={`tk-value-${fkey}`}>
          {value || <span className="text-neutral-500 italic">— pa vlerë —</span>}
        </div>
      ) : multiline ? (
        <textarea value={draft[fkey] ?? ""} onChange={e => setDraft(prev => ({ ...prev, [fkey]: e.target.value }))}
          placeholder={placeholder} rows={3} data-testid={`tk-input-${fkey}`}
          className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 text-sm focus:border-[#FFD600] outline-none resize-y" />
      ) : (
        <input value={draft[fkey] ?? ""} onChange={e => setDraft(prev => ({ ...prev, [fkey]: e.target.value }))}
          placeholder={placeholder} data-testid={`tk-input-${fkey}`}
          className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 text-sm focus:border-[#FFD600] outline-none" />
      )}
      {hint && <div className="text-[11px] text-neutral-500 mt-2">{hint}</div>}
    </div>
  );
};

const TekstetAdmin = () => {
  const [s, setS] = useState(null);
  const [editing, setEditing] = useState({});
  const [draft, setDraft] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [maintDraft, setMaintDraft] = useState("");
  const [maintSaving, setMaintSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(r => {
      setS(r.data); setDraft(r.data);
      setMaintDraft(r.data.maintenance_message || "");
    }).catch(() => toast.error("Gabim gjatë ngarkimit"));
  }, []);

  const startEdit = (key) => { setEditing(prev => ({ ...prev, [key]: true })); setDraft(prev => ({ ...prev, [key]: s?.[key] || "" })); };
  const cancelEdit = (key) => { setEditing(prev => ({ ...prev, [key]: false })); setDraft(prev => ({ ...prev, [key]: s?.[key] || "" })); };
  const saveField = async (key) => {
    setSavingKey(key);
    try {
      await api.put("/admin/settings", { [key]: draft[key] || "" });
      setS(prev => ({ ...prev, [key]: draft[key] || "" }));
      setEditing(prev => ({ ...prev, [key]: false }));
      toast.success("U ruajt");
    } catch { toast.error("Gabim gjatë ruajtjes"); }
    setSavingKey(null);
  };

  const toggleMaintenance = async () => {
    setMaintSaving(true);
    try {
      const newVal = !s.maintenance_mode;
      await api.put("/admin/settings", { maintenance_mode: newVal, maintenance_message: maintDraft });
      setS(prev => ({ ...prev, maintenance_mode: newVal, maintenance_message: maintDraft }));
      toast.success(newVal ? "Faqja u pauzua" : "Faqja u aktivizua");
    } catch { toast.error("Gabim"); }
    setMaintSaving(false);
  };

  const saveMaintMessage = async () => {
    setMaintSaving(true);
    try {
      await api.put("/admin/settings", { maintenance_message: maintDraft });
      setS(prev => ({ ...prev, maintenance_message: maintDraft }));
      toast.success("Mesazhi u ruajt");
    } catch { toast.error("Gabim"); }
    setMaintSaving(false);
  };

  if (!s) return <div className="text-neutral-400 text-sm">Duke ngarkuar...</div>;

  const fieldProps = { s, editing, draft, savingKey, startEdit, cancelEdit, saveField, setDraft };
  const maintOn = !!s.maintenance_mode;

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl uppercase mb-2">Tekstet e Faqes Kryesore</h1>
      <p className="text-sm text-neutral-400 mb-5">Të gjitha tekstet e shfaqura te faqja <code className="text-[#FFD600]">/</code> (Ballina) mund të ndryshohen këtu.</p>

      {/* Maintenance mode */}
      <div className={`hg-card p-5 mb-6 border-2 ${maintOn ? "border-red-500 bg-red-950/20" : "border-[#FFD600]/30"}`} data-testid="maintenance-card">
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-full ${maintOn ? "bg-red-500 text-white" : "bg-[#FFD600]/10 text-[#FFD600]"}`}>
            <Power size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest font-bold mb-1" style={{ color: maintOn ? "#f87171" : "#FFD600" }}>
              {maintOn ? "Faqja është pauzuar" : "Modaliteti: Pauzë e faqes"}
            </div>
            <p className="text-sm text-neutral-300 mb-3">
              {maintOn
                ? "Aktualisht klientët po shohin një faqe rregullimi. Vetëm admini mund të hyjë. Kliko për ta aktivizuar përsëri."
                : "Kur pauzohet, klientët shohin një faqe të thjeshtë me mesazhin tënd — nuk mund të shfletojnë produkte apo të bëjnë porosi. Ti si admin hyn normalisht."}
            </p>
            <div className="mb-3">
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1 block">Mesazhi për klientët</label>
              <textarea value={maintDraft} onChange={e => setMaintDraft(e.target.value)} rows={2}
                data-testid="maint-msg-input"
                placeholder="Faqja është në rregullim. Kthehemi së shpejti."
                className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 text-sm focus:border-[#FFD600] outline-none resize-y" />
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={toggleMaintenance} disabled={maintSaving} data-testid="maint-toggle"
                className={`px-4 py-2.5 uppercase text-xs font-bold tracking-wider inline-flex items-center gap-2 disabled:opacity-50 ${maintOn ? "bg-emerald-500 text-black hover:bg-emerald-400" : "bg-red-500 text-white hover:bg-red-400"}`}>
                <Power size={14} />
                {maintSaving ? "..." : (maintOn ? "▶ Aktivizo faqen" : "⏸ Pauzo faqen")}
              </button>
              <button onClick={saveMaintMessage} disabled={maintSaving} data-testid="maint-save-msg"
                className="px-4 py-2.5 uppercase text-xs tracking-wider border border-[#1F2B44] hover:border-[#FFD600] hover:text-[#FFD600] inline-flex items-center gap-2 disabled:opacity-50">
                <Save size={14} /> Ruaj mesazhin
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3 mb-6">
        <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold pl-1">Hero (Pjesa e sipërme)</div>
        <TekField {...fieldProps} fkey="hp_hero_badge" label="Etiketa e verdhë" placeholder="HIDRAULIKË • TUBA • KOPLINGË" />
        <TekField {...fieldProps} fkey="hp_hero_title" label="Titulli kryesor (pjesa e bardhë)" placeholder="Zgjidhja juaj për" />
        <TekField {...fieldProps} fkey="hp_hero_title_accent" label="Titulli — theksuar (i verdhë)" placeholder="hidraulikë!" hint="Kjo pjesë shfaqet me ngjyrë të verdhë menjëherë pas titullit kryesor." />
        <TekField {...fieldProps} fkey="hp_hero_subtitle" label="Nën-titulli" placeholder="Cilësi e garantuar • Çmime konkurruese • Shërbim i shpejtë në Kosovë" multiline />
        <TekField {...fieldProps} fkey="hp_cta_configurator" label="Butoni — Konfigurator" placeholder="Konfiguro Tubin" />
        <TekField {...fieldProps} fkey="hp_cta_photo" label="Butoni — Kërko me foto" placeholder="Kërko pjesën me foto" />
        <TekField {...fieldProps} fkey="hp_search_placeholder" label="Placeholder i kërkimit" placeholder="Çfarë po kërkon?" />
      </div>

      <div className="space-y-3 mb-6">
        <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold pl-1">Kartat e vlerave (4 shirita)</div>
        <TekField {...fieldProps} fkey="hp_benefit_1" label="Karta 1 — Dërgesa" placeholder="Dërgesë e shpejtë në gjithë Kosovën" />
        <TekField {...fieldProps} fkey="hp_benefit_2" label="Karta 2 — Cilësia" placeholder="Produkte cilësore" />
        <TekField {...fieldProps} fkey="hp_benefit_3" label="Karta 3 — Mbështetja" placeholder="Mbështetje teknike" />
        <TekField {...fieldProps} fkey="hp_benefit_4" label="Karta 4 — Garancia" placeholder="Garanci për produktet" />
      </div>

      <div className="space-y-3 mb-6">
        <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold pl-1">Seksionet</div>
        <TekField {...fieldProps} fkey="hp_categories_title" label="Titulli — Kategoritë" placeholder="Kategoritë kryesore" />
        <TekField {...fieldProps} fkey="hp_view_all" label="Linku — Shiko të gjitha" placeholder="Shiko të gjitha" />
        <TekField {...fieldProps} fkey="hp_featured_title" label="Titulli — Produktet e paraqitura" placeholder="Produktet më të shitura" />
      </div>

      <div className="space-y-3 mb-6">
        <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold pl-1">Banneri — Kërko me foto</div>
        <TekField {...fieldProps} fkey="hp_photo_banner_title" label="Titulli i bannerit" placeholder="Nuk e di cilën pjesë të duhet?" />
        <TekField {...fieldProps} fkey="hp_photo_banner_subtitle" label="Nën-titulli i bannerit" placeholder="Dërgo fotografinë..." multiline />
        <TekField {...fieldProps} fkey="hp_photo_banner_cta" label="Butoni i bannerit" placeholder="Dërgo Foto" />
      </div>
    </div>
  );
};

const ConfiguratorAdmin = () => {
  const [tab, setTab] = useState("hoses");
  return (
    <div>
      <h1 className="font-display text-3xl uppercase mb-4">Konfigurator</h1>
      <div className="flex gap-1 mb-5 border-b border-[#1F2B44] overflow-x-auto no-scrollbar">
        <button onClick={() => setTab("hoses")} data-testid="tab-hoses"
          className={`px-4 py-2.5 text-sm uppercase tracking-wider border-b-2 whitespace-nowrap ${tab === "hoses" ? "border-[#FFD600] text-[#FFD600]" : "border-transparent text-neutral-400"}`}>Gypi</button>
        <button onClick={() => setTab("ferrules")} data-testid="tab-ferrules"
          className={`px-4 py-2.5 text-sm uppercase tracking-wider border-b-2 whitespace-nowrap ${tab === "ferrules" ? "border-[#FFD600] text-[#FFD600]" : "border-transparent text-neutral-400"}`}>Hylsa</button>
        <button onClick={() => setTab("couplings")} data-testid="tab-couplings"
          className={`px-4 py-2.5 text-sm uppercase tracking-wider border-b-2 whitespace-nowrap ${tab === "couplings" ? "border-[#FFD600] text-[#FFD600]" : "border-transparent text-neutral-400"}`}>Kopllinga</button>
      </div>
      {tab === "hoses" && <HoseTypesTab />}
      {tab === "ferrules" && <FerrulesTab />}
      {tab === "couplings" && <CouplingsTab />}
    </div>
  );
};


const PersoneliPage = () => {
  const [tab, setTab] = useState("vac");
  const [vacs, setVacs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [salWorker, setSalWorker] = useState(null);
  const [salaries, setSalaries] = useState([]);
  const [salForm, setSalForm] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, base_salary: 500, hours_normal: 160, hours_extra: 0, bonuses: 0, deductions: 0, paid: 0, paid_at: "", note: "" });
  const [decideModal, setDecideModal] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [tsData, setTsData] = useState(null);
  const [tsLoading, setTsLoading] = useState(false);

  const load = async () => {
    const [v, w] = await Promise.all([
      api.get("/admin/vacations"),
      api.get("/admin/workers"),
    ]);
    setVacs(v.data); setWorkers(w.data);
  };
  useEffect(() => { load(); }, []);

  const fmtDurAdm = (h) => {
    if (!h && h !== 0) return "0h 0m";
    const hh = Math.floor(h); const mm = Math.round((h - hh) * 60);
    return `${hh}h ${mm}m`;
  };
  const fmtTimeAdm = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleTimeString("sq-AL", { hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
  };

  const loadSalaries = async (worker) => {
    setSalWorker(worker);
    const r = await api.get(`/admin/workers/${worker.id}/salaries`);
    setSalaries(r.data);
    // Auto-fetch hours for current period
    fetchHoursForSalary(worker.id, salForm.year, salForm.month, worker);
  };

  const fetchHoursForSalary = async (workerId, year, month, workerObj = null) => {
    try {
      const r = await api.get(`/admin/workers/${workerId}/monthly-hours?year=${year}&month=${month}`);
      const w = workerObj || salWorker;
      const baseSalary = w?.monthly_salary != null ? parseFloat(w.monthly_salary) : salForm.base_salary;
      setSalForm(prev => ({
        ...prev, year, month,
        hours_normal: r.data.hours_regular,
        hours_extra: r.data.hours_overtime,
        base_salary: baseSalary,
      }));
    } catch (e) { /* ignore */ }
  };

  const loadTimesheet = async (worker, y, m) => {
    setSalWorker(worker); setTsLoading(true);
    try {
      const r = await api.get(`/admin/workers/${worker.id}/timesheet?year=${y}&month=${m}`);
      setTsData({ ...r.data, year: y, month: m });
    } finally { setTsLoading(false); }
  };

  const decide = async (status) => {
    if (!decideModal) return;
    await api.put(`/admin/vacations/${decideModal.id}/decide`, { status, admin_note: adminNote });
    toast.success(status === "approved" ? "Aprovuar" : "Refuzuar");
    setDecideModal(null); setAdminNote(""); load();
  };

  const saveSalary = async () => {
    if (!salWorker) return;
    try {
      await api.post(`/admin/workers/${salWorker.id}/salaries`, salForm);
      toast.success("U ruajt paga");
      loadSalaries(salWorker);
    } catch (e) { toast.error(e.response?.data?.detail || "Gabim"); }
  };

  const MONTHS = ["", "Janar","Shkurt","Mars","Prill","Maj","Qershor","Korrik","Gusht","Shtator","Tetor","Nëntor","Dhjetor"];

  return (
    <div>
      <h1 className="font-display text-3xl uppercase mb-4">Personeli — Pushime, Orë & Paga</h1>
      <div className="grid grid-cols-3 gap-1 mb-4 p-1 bg-[#131E30] border border-[#1F2B44]">
        <button onClick={() => setTab("vac")} data-testid="admin-tab-vac"
          className={`py-2.5 text-xs uppercase tracking-widest font-bold ${tab === "vac" ? "bg-[#FFD600] text-black" : "text-neutral-400"}`}>
          Pushime ({vacs.filter(v => v.status === "pending").length})
        </button>
        <button onClick={() => setTab("time")} data-testid="admin-tab-time"
          className={`py-2.5 text-xs uppercase tracking-widest font-bold ${tab === "time" ? "bg-[#FFD600] text-black" : "text-neutral-400"}`}>
          Orët e punës
        </button>
        <button onClick={() => setTab("sal")} data-testid="admin-tab-sal"
          className={`py-2.5 text-xs uppercase tracking-widest font-bold ${tab === "sal" ? "bg-[#FFD600] text-black" : "text-neutral-400"}`}>
          Paga
        </button>
      </div>

      {tab === "vac" && (
        <div className="space-y-2">
          {vacs.length === 0 && <div className="hg-card p-6 text-center text-neutral-400">Ende s'ka kërkesa</div>}
          {vacs.map(v => (
            <div key={v.id} className="hg-card p-3" data-testid={`admin-vac-${v.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">{v.worker_name}</div>
                  <div className="text-xs text-neutral-400">{v.start_date} → {v.end_date} ({v.days} ditë)</div>
                  {v.reason && <div className="text-xs text-neutral-300 mt-1">"{v.reason}"</div>}
                  {v.admin_note && <div className="text-xs text-neutral-400 mt-1 italic">Shënim: {v.admin_note}</div>}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <div className={`text-[10px] uppercase font-bold px-2 py-0.5 border ${v.status === "pending" ? "text-orange-400 border-orange-500/40" : v.status === "approved" ? "text-emerald-400 border-emerald-500/40" : "text-red-400 border-red-500/40"}`}>
                    {v.status === "pending" ? "Në pritje" : v.status === "approved" ? "Aprovuar" : "Refuzuar"}
                  </div>
                  {v.status === "pending" && (
                    <button onClick={() => { setDecideModal(v); setAdminNote(""); }} data-testid={`vac-decide-${v.id}`}
                      className="text-[10px] uppercase px-2 py-1 bg-[#FFD600] text-black font-bold">Vendos</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "time" && (
        <div className="grid md:grid-cols-[240px_1fr] gap-3">
          <div className="hg-card p-3">
            <div className="text-[10px] uppercase text-neutral-400 mb-2">Punëtorët</div>
            {workers.map(w => (
              <button key={w.id} onClick={() => loadTimesheet(w, new Date().getFullYear(), new Date().getMonth() + 1)} data-testid={`time-w-${w.id}`}
                className={`w-full text-left p-2 text-sm ${salWorker?.id === w.id && tab === "time" ? "bg-[#FFD600]/10 text-[#FFD600]" : "hover:bg-[#0B1220]"}`}>
                {w.first_name} {w.last_name}
              </button>
            ))}
          </div>
          <div>
            {!salWorker ? (
              <div className="hg-card p-6 text-center text-neutral-400">Zgjidh një punëtor</div>
            ) : (
              <>
                <div className="hg-card p-3 mb-3 flex items-center gap-2 flex-wrap">
                  <div className="font-display text-lg flex-1 min-w-0">{salWorker.first_name} {salWorker.last_name}</div>
                  <select value={tsData?.month || new Date().getMonth() + 1}
                    onChange={e => loadTimesheet(salWorker, tsData?.year || new Date().getFullYear(), parseInt(e.target.value))}
                    data-testid="admin-ts-month"
                    className="bg-[#131E30] border border-[#1F2B44] px-2 py-1.5 text-sm">
                    {MONTHS.map((m, i) => i > 0 && <option key={i} value={i}>{m}</option>)}
                  </select>
                  <input type="number" value={tsData?.year || new Date().getFullYear()}
                    onChange={e => loadTimesheet(salWorker, parseInt(e.target.value), tsData?.month || new Date().getMonth() + 1)}
                    data-testid="admin-ts-year"
                    className="w-20 bg-[#131E30] border border-[#1F2B44] px-2 py-1.5 text-sm" />
                </div>
                {tsLoading ? (
                  <div className="text-center py-6 text-neutral-400">Duke ngarkuar...</div>
                ) : tsData && (
                  <>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="hg-card p-3 text-center"><div className="text-[9px] uppercase text-neutral-400">Orë Normale</div><div className="text-xl font-display text-emerald-400">{tsData.hours_regular}</div></div>
                      <div className="hg-card p-3 text-center"><div className="text-[9px] uppercase text-neutral-400">Orë Shtesë</div><div className="text-xl font-display text-orange-400">{tsData.hours_overtime}</div></div>
                      <div className="hg-card p-3 text-center"><div className="text-[9px] uppercase text-neutral-400">Total</div><div className="text-xl font-display text-[#FFD600]">{tsData.hours_total}</div></div>
                    </div>
                    <div className="hg-card overflow-x-auto">
                      {tsData.entries.length === 0 ? (
                        <div className="p-6 text-center text-neutral-500 text-sm">S'ka regjistrime për këtë muaj</div>
                      ) : (
                        <table className="w-full text-sm">
                          <thead className="text-[10px] uppercase text-neutral-400 border-b border-[#1F2B44]">
                            <tr><th className="text-left px-2 py-2">Data</th><th className="text-left px-2 py-2">Hyrja</th><th className="text-left px-2 py-2">Dalja</th><th className="text-right px-2 py-2">Orë</th><th className="text-left px-2 py-2">Burimi</th></tr>
                          </thead>
                          <tbody>
                            {tsData.entries.map(e => (
                              <tr key={e.id} className="border-b border-[#1F2B44]/50" data-testid={`admin-ts-${e.id}`}>
                                <td className="px-2 py-2 text-xs">{e.date}</td>
                                <td className="px-2 py-2 text-xs">{fmtTimeAdm(e.clock_in)}</td>
                                <td className="px-2 py-2 text-xs">{e.clock_out ? fmtTimeAdm(e.clock_out) : <span className="text-emerald-400">Në vazhdim</span>}</td>
                                <td className="px-2 py-2 text-xs text-right font-bold">{fmtDurAdm(e.duration_hours)}</td>
                                <td className="px-2 py-2 text-[10px] uppercase text-neutral-500">{e.source}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {tab === "sal" && (
        <div className="grid md:grid-cols-[240px_1fr] gap-3">
          <div className="hg-card p-3">
            <div className="text-[10px] uppercase text-neutral-400 mb-2">Punëtorët</div>
            {workers.map(w => (
              <button key={w.id} onClick={() => loadSalaries(w)} data-testid={`sal-w-${w.id}`}
                className={`w-full text-left p-2 text-sm ${salWorker?.id === w.id ? "bg-[#FFD600]/10 text-[#FFD600]" : "hover:bg-[#0B1220]"}`}>
                {w.first_name} {w.last_name}
              </button>
            ))}
          </div>
          <div>
            {!salWorker ? (
              <div className="hg-card p-6 text-center text-neutral-400">Zgjidh një punëtor</div>
            ) : (
              <>
                <div className="hg-card p-3 mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase text-[#FFD600] font-bold">Regjistro pagë për {salWorker.first_name}</div>
                    <button onClick={() => fetchHoursForSalary(salWorker.id, salForm.year, salForm.month)} data-testid="sal-refresh-hours"
                      className="text-[10px] uppercase px-2 py-1 border border-[#1F2B44] hover:border-[#FFD600] hover:text-[#FFD600]">↻ Merr orët</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <select value={salForm.month} onChange={e => { const m = parseInt(e.target.value); setSalForm({...salForm, month: m}); fetchHoursForSalary(salWorker.id, salForm.year, m); }} data-testid="sal-month" className="bg-[#131E30] border border-[#1F2B44] px-2 py-1.5">
                      {MONTHS.map((m, i) => i > 0 && <option key={i} value={i}>{m}</option>)}
                    </select>
                    <input type="number" value={salForm.year} onChange={e => { const y = parseInt(e.target.value); setSalForm({...salForm, year: y}); fetchHoursForSalary(salWorker.id, y, salForm.month); }} data-testid="sal-year" className="bg-[#131E30] border border-[#1F2B44] px-2 py-1.5" />
                    <label className="col-span-2 text-[10px] uppercase text-neutral-400 mt-1">Paga bazë (€)</label>
                    <input type="number" step="0.01" value={salForm.base_salary} onChange={e => setSalForm({...salForm, base_salary: parseFloat(e.target.value) || 0})} data-testid="sal-base" className="col-span-2 bg-[#131E30] border border-[#1F2B44] px-2 py-1.5" />
                    <div><label className="text-[10px] uppercase text-neutral-400">Orë normale (auto)</label><input type="number" step="0.01" value={salForm.hours_normal} onChange={e => setSalForm({...salForm, hours_normal: parseFloat(e.target.value) || 0})} data-testid="sal-hn" className="w-full bg-[#131E30] border border-emerald-500/40 px-2 py-1.5" /></div>
                    <div><label className="text-[10px] uppercase text-neutral-400">Orë shtesë (auto)</label><input type="number" step="0.01" value={salForm.hours_extra} onChange={e => setSalForm({...salForm, hours_extra: parseFloat(e.target.value) || 0})} data-testid="sal-he" className="w-full bg-[#131E30] border border-orange-500/40 px-2 py-1.5" /></div>
                    <div><label className="text-[10px] uppercase text-neutral-400">Shtesa (€)</label><input type="number" step="0.01" value={salForm.bonuses} onChange={e => setSalForm({...salForm, bonuses: parseFloat(e.target.value) || 0})} className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-1.5" /></div>
                    <div><label className="text-[10px] uppercase text-neutral-400">Zbritje (€)</label><input type="number" step="0.01" value={salForm.deductions} onChange={e => setSalForm({...salForm, deductions: parseFloat(e.target.value) || 0})} className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-1.5" /></div>
                    <div><label className="text-[10px] uppercase text-neutral-400">Paguar (€)</label><input type="number" step="0.01" value={salForm.paid} onChange={e => setSalForm({...salForm, paid: parseFloat(e.target.value) || 0})} data-testid="sal-paid" className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-1.5" /></div>
                    <div><label className="text-[10px] uppercase text-neutral-400">Data pagesës</label><input type="date" value={salForm.paid_at || ""} onChange={e => setSalForm({...salForm, paid_at: e.target.value})} className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-1.5" /></div>
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-2">💡 Orët tërhiqen automatikisht nga clock-in/out i punëtorit. Nëse s'përputhen, ndrysho manualisht më lart.</div>
                  <button onClick={saveSalary} data-testid="sal-save" className="mt-3 hg-btn-primary py-2 px-4 text-xs uppercase w-full">Ruaj pagën</button>
                </div>
                <div className="hg-card p-3">
                  <div className="text-xs uppercase text-neutral-400 font-bold mb-2">Historiku i pagave</div>
                  <div className="space-y-1">
                    {salaries.length === 0 && <div className="text-neutral-500 text-sm italic p-2">S'ka regjistrim ende</div>}
                    {salaries.map(s => (
                      <div key={s.id} className="flex items-center justify-between p-2 bg-[#0B1220]/40 border border-[#1F2B44] text-xs">
                        <div><div className="font-bold">{MONTHS[s.month]} {s.year}</div><div className="text-neutral-500">Orë: {s.hours_normal}+{s.hours_extra} • Total: {fmtEUR(s.total)}</div></div>
                        <div className="text-right">
                          <div className={s.status === "paid" ? "text-emerald-400" : s.status === "partial" ? "text-orange-400" : "text-red-400"}>{s.status === "paid" ? "E paguar" : s.status === "partial" ? "Pjesërisht" : "E papaguar"}</div>
                          <div className="text-neutral-500">{fmtEUR(s.paid)} / {fmtEUR(s.total)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {decideModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-3" onClick={() => setDecideModal(null)}>
          <div className="hg-card p-4 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="font-display text-lg mb-2">Vendos për kërkesën</div>
            <div className="text-sm mb-2">{decideModal.worker_name}: {decideModal.start_date} → {decideModal.end_date} ({decideModal.days} ditë)</div>
            <textarea value={adminNote} onChange={e => setAdminNote(e.target.value)} placeholder="Arsyeja (opsional, e detyrueshme për refuzim)"
              data-testid="decide-note"
              rows={3} className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-1.5 text-sm mb-3" />
            <div className="flex gap-2">
              <button onClick={() => decide("approved")} data-testid="decide-approve" className="flex-1 py-2 bg-emerald-500 text-black font-bold text-xs uppercase">✓ Aprovo</button>
              <button onClick={() => decide("rejected")} data-testid="decide-reject" className="flex-1 py-2 bg-red-500 text-white font-bold text-xs uppercase">✗ Refuzo</button>
              <button onClick={() => setDecideModal(null)} className="px-3 border border-[#1F2B44] text-xs uppercase">Anulo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BonusesPage = () => {
  const [tiers, setTiers] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    const [t, c] = await Promise.all([
      api.get("/admin/loyalty/tiers"),
      api.get("/admin/loyalty/clients"),
    ]);
    setTiers(t.data.tiers || []);
    setClients(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const searchClients = async (query) => {
    const r = await api.get(`/admin/loyalty/clients${query ? `?q=${encodeURIComponent(query)}` : ""}`);
    setClients(r.data || []);
  };

  const addTier = () => setTiers([...tiers, { id: `new-${Date.now()}`, min_spend: 100, discount_pct: 5, label: "", active: true }]);
  const updateTier = (idx, key, val) => {
    const next = [...tiers];
    next[idx] = { ...next[idx], [key]: val };
    setTiers(next);
  };
  const removeTier = (idx) => setTiers(tiers.filter((_, i) => i !== idx));
  const saveTiers = async () => {
    setSaving(true);
    try {
      const payload = tiers.map(t => ({
        id: t.id?.startsWith("new-") ? null : t.id,
        min_spend: parseFloat(t.min_spend) || 0,
        discount_pct: parseFloat(t.discount_pct) || 0,
        label: t.label || "",
        active: t.active !== false,
      }));
      const r = await api.put("/admin/loyalty/tiers", { tiers: payload });
      setTiers(r.data.tiers || []);
      toast.success("Bonuset u ruajtën");
    } catch { toast.error("Gabim gjatë ruajtjes"); }
    setSaving(false);
  };

  const viewClient = async (id) => {
    const r = await api.get(`/admin/loyalty/clients/${id}`);
    setDetail(r.data);
  };
  const resetBonus = async (id) => {
    if (!window.confirm("Rivendos bonuset e përdorura për këtë klient? Ai do të mund t'i përdorë sërish.")) return;
    await api.post(`/admin/loyalty/clients/${id}/reset-bonus`);
    toast.success("U rivendosën");
    viewClient(id);
    load();
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#FFD600]/15 border border-[#FFD600]/40 flex items-center justify-center">
          <Gift size={20} className="text-[#FFD600]" />
        </div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl uppercase">Bonuset & Loyalty</h1>
          <div className="text-xs text-neutral-400">Konfiguro nivelet dhe menaxho klientët</div>
        </div>
      </div>

      {/* Tiers editor */}
      <div className="hg-card p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-[#FFD600] font-bold">Nivelet e Bonusit</div>
          <button onClick={saveTiers} disabled={saving} data-testid="loyalty-save-tiers"
            className="hg-btn-primary px-3 py-1.5 text-xs uppercase inline-flex items-center gap-1"><Save size={12} /> Ruaj</button>
        </div>
        <div className="text-xs text-neutral-400 mb-3">Çdo nivel: kur klienti kalon shumën minimale, fiton bonusin (një përdorim).</div>
        <div className="space-y-2">
          {tiers.length === 0 && <div className="text-xs text-neutral-500 italic p-3 text-center">Ende s'ka nivele. Shto të parën.</div>}
          {tiers.map((t, idx) => (
            <div key={t.id || idx} className="grid grid-cols-12 gap-2 items-center bg-[#0B1220]/40 p-2 border border-[#1F2B44]">
              <div className="col-span-4">
                <label className="text-[9px] uppercase text-neutral-500">Shpenzim min (€)</label>
                <input type="number" step="0.01" value={t.min_spend} onChange={e => updateTier(idx, "min_spend", e.target.value)}
                  data-testid={`tier-min-${idx}`}
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-1.5 text-sm" />
              </div>
              <div className="col-span-3">
                <label className="text-[9px] uppercase text-neutral-500">Zbritje %</label>
                <input type="number" step="0.1" value={t.discount_pct} onChange={e => updateTier(idx, "discount_pct", e.target.value)}
                  data-testid={`tier-pct-${idx}`}
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-1.5 text-sm" />
              </div>
              <div className="col-span-4">
                <label className="text-[9px] uppercase text-neutral-500">Etiketa (opsional)</label>
                <input value={t.label || ""} onChange={e => updateTier(idx, "label", e.target.value)}
                  placeholder="p.sh. Bronze"
                  data-testid={`tier-label-${idx}`}
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-1.5 text-sm" />
              </div>
              <div className="col-span-1 flex justify-end pt-4">
                <button onClick={() => removeTier(idx)} data-testid={`tier-del-${idx}`}
                  className="p-1.5 text-red-400 hover:bg-red-500/10 border border-red-500/30"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
        <button onClick={addTier} data-testid="loyalty-add-tier"
          className="mt-2 text-xs uppercase tracking-wider px-3 py-2 border border-[#FFD600]/40 text-[#FFD600] hover:bg-[#FFD600]/10 inline-flex items-center gap-1"><Plus size={12} /> Shto nivel</button>
      </div>

      {/* Clients list */}
      <div className="hg-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-cyan-400 font-bold">Klientët & Blerjet</div>
          <div className="text-[10px] text-neutral-500">{clients.length} klientë</div>
        </div>
        <div className="mb-3 relative">
          <SearchIcon size={14} className="absolute left-3 top-3 text-neutral-500" />
          <input value={q} onChange={e => { setQ(e.target.value); searchClients(e.target.value); }}
            placeholder="Kërko me emër, email, numër klient..."
            data-testid="loyalty-search"
            className="w-full bg-[#131E30] border border-[#1F2B44] pl-9 pr-3 py-2 text-sm" />
        </div>
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {clients.map(c => (
            <button key={c.id} onClick={() => viewClient(c.id)} data-testid={`loyalty-client-${c.id}`}
              className="w-full flex items-center gap-3 p-2 hover:bg-[#131E30] text-left border-b border-[#1F2B44]/50">
              <div className="w-9 h-9 rounded-full bg-[#FFD600]/10 flex items-center justify-center font-bold text-[#FFD600] flex-shrink-0">
                {(c.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{c.name}</div>
                <div className="text-[10px] text-neutral-500 font-mono">{c.client_number} • {c.email}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-bold text-emerald-400">{fmtEUR(c.lifetime_spend || 0)}</div>
                <div className="text-[10px] text-neutral-500">{c.invoices_count || 0} fatura</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Client detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 overflow-y-auto" onClick={() => setDetail(null)}>
          <div className="hg-card w-full max-w-2xl my-4" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-[#1F2B44] flex items-center justify-between">
              <div>
                <div className="text-xs uppercase text-neutral-400">Klienti</div>
                <div className="font-display text-xl">{detail.name}</div>
                <div className="text-[10px] font-mono text-[#FFD600]">{detail.client_number}</div>
              </div>
              <button onClick={() => setDetail(null)} className="p-2 hover:bg-[#131E30]"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="flex flex-col sm:flex-row items-center gap-4 bg-[#0B1220]/50 p-4 border border-[#1F2B44]">
                <div className="bg-white p-2">
                  <img src={`${apiBase}/api/admin/qr?data=${encodeURIComponent(detail.qr_token)}&size=6`} alt="QR" className="w-32 h-32" />
                </div>
                <div className="flex-1 text-sm space-y-1">
                  <div><span className="text-neutral-400 text-xs">Email:</span> {detail.email}</div>
                  <div><span className="text-neutral-400 text-xs">Telefon:</span> {detail.phone || "—"}</div>
                  <div className="pt-2 border-t border-[#1F2B44]">
                    <div className="text-neutral-400 text-xs">Blerje totale</div>
                    <div className="font-display text-2xl text-emerald-400">{fmtEUR(detail.lifetime_spend)}</div>
                  </div>
                  {detail.active_tier && (
                    <div className="text-xs text-emerald-400"><Gift size={12} className="inline" /> Bonus aktiv: {detail.active_tier.discount_pct}%</div>
                  )}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase text-neutral-400 font-bold">Bonuset e përdorura ({(detail.used_tier_ids || []).length})</div>
                  {(detail.used_tier_ids || []).length > 0 && (
                    <button onClick={() => resetBonus(detail.id)} data-testid="loyalty-reset"
                      className="text-[10px] uppercase px-2 py-1 border border-orange-500/40 text-orange-400 hover:bg-orange-500/10">Rivendos</button>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-neutral-400 font-bold mb-2">Historiku i faturave ({(detail.invoices || []).length})</div>
                <div className="space-y-1 text-xs">
                  {(detail.invoices || []).length === 0 && <div className="text-neutral-500 italic p-3 text-center">Ende s'ka fatura</div>}
                  {(detail.invoices || []).map(inv => (
                    <div key={inv.id} className="flex items-center justify-between p-2 bg-[#0B1220]/40 border border-[#1F2B44]">
                      <div>
                        <div className="font-mono font-bold">{inv.invoice_number}</div>
                        <div className="text-[10px] text-neutral-500">{new Date(inv.created_at).toLocaleString("sq-AL")}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-[#FFD600]">{fmtEUR(inv.total)}</div>
                        {inv.bonus_amount > 0 && <div className="text-[10px] text-emerald-400">−{fmtEUR(inv.bonus_amount)} bonus</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const { user } = useApp();
  const nav = useNavigate();
  useEffect(() => { if (user === false || (user === null && !localStorage.getItem("hg_token"))) nav("/login"); }, [user, nav]);
  if (!user) return <div className="p-8 text-center text-neutral-400">Duke u ngarkuar...</div>;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid md:grid-cols-[220px_1fr] gap-6">
      <AdminNav />
      <div className="min-w-0">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="produktet" element={<Products />} />
          <Route path="alarm-stoku" element={<LowStockAlert />} />
          <Route path="dollapet" element={<WarehouseMap />} />
          <Route path="kategorite" element={<CategoriesAdmin />} />
          <Route path="konfigurator" element={<ConfiguratorAdmin />} />
          <Route path="porosite" element={<Orders />} />
          <Route path="porosite/:id" element={<OrderDetail />} />
          <Route path="klientet" element={<Customers />} />
          <Route path="bonuset" element={<BonusesPage />} />
          <Route path="punetoret" element={<WorkersAdmin />} />
          <Route path="personeli" element={<PersoneliPage />} />
          <Route path="pos-raport" element={<PosReportAdmin />} />
          <Route path="kerkesat" element={<PartRequests />} />
          <Route path="kontakt" element={<KontaktAdmin />} />
          <Route path="tekstet" element={<TekstetAdmin />} />
          <Route path="cilesimet" element={<Cilesimet />} />
        </Routes>
      </div>
    </div>
  );
}


const CONTRACT_TYPES = [
  { v: "permanent", l: "E përhershme" },
  { v: "probation", l: "Provë" },
  { v: "fixed_1y", l: "1 vjeçar" },
  { v: "other", l: "Tjetër" },
];

const WorkersAdmin = () => {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const empty = {
    first_name: "", last_name: "", pos_code: "", active: true,
    phone: "", email: "", address: "", iban: "", bank_name: "", personal_id: "",
    position: "", contract_type: "permanent", hire_date: "", contract_end_date: "",
    hours_per_day: 8, hours_per_week: 40, vacation_days_total: 21,
    monthly_salary: 0, hourly_rate: 0, overtime_rate: 0, notes: "",
  };

  const load = () => { setLoading(true); api.get("/admin/workers").then(r => setWorkers(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const genCode = () => setEdit(prev => ({ ...(prev || empty), pos_code: String(Math.floor(100000 + Math.random() * 900000)) }));

  const openNew = () => setEdit({ ...empty, pos_code: String(Math.floor(100000 + Math.random() * 900000)) });

  const save = async () => {
    if (!edit.first_name.trim() || !edit.last_name.trim()) { toast.error("Emri dhe mbiemri janë të detyrueshëm"); return; }
    if (!/^\d{6}$/.test(edit.pos_code)) { toast.error("Kodi duhet të jetë 6 shifra"); return; }
    try {
      const payload = { ...edit };
      // Ensure numeric fields are numbers
      ["hours_per_day","hours_per_week","vacation_days_total","monthly_salary","hourly_rate","overtime_rate"].forEach(k => {
        payload[k] = parseFloat(payload[k]) || 0;
      });
      if (edit.id) await api.put(`/admin/workers/${edit.id}`, payload);
      else await api.post("/admin/workers", payload);
      toast.success("U ruajt"); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gabim"); }
  };
  const del = async (id) => {
    if (!window.confirm("Fshi këtë punëtor? Faturat e tij mbeten të ruajtura.")) return;
    await api.delete(`/admin/workers/${id}`); load(); toast.success("U fshi");
  };
  const toggleActive = async (w) => {
    await api.put(`/admin/workers/${w.id}`, { active: !w.active });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-3xl uppercase">Punëtorët</h1>
        <button onClick={openNew}
          data-testid="worker-add" className="hg-btn-primary px-4 py-2 uppercase text-xs inline-flex items-center gap-1">
          <Plus size={14} /> Shto punëtor
        </button>
      </div>
      <p className="text-sm text-neutral-400 mb-4">Punëtorët hyjnë te <code className="text-[#FFD600]">/arka/login</code> vetëm me kodin 6-shifror. Nuk kanë qasje në Admin.</p>

      {loading ? (
        <div className="text-center py-10 text-neutral-400">Duke ngarkuar...</div>
      ) : workers.length === 0 ? (
        <div className="hg-card p-10 text-center text-neutral-500">Ende s'ka punëtorë. Shto të parin!</div>
      ) : (
        <div className="hg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-neutral-400 border-b border-[#1F2B44]">
              <tr>
                <th className="text-left px-3 py-2">Emri</th>
                <th className="text-left px-3 py-2">Pozicioni</th>
                <th className="text-left px-3 py-2">Kodi</th>
                <th className="text-left px-3 py-2">Telefon</th>
                <th className="text-left px-3 py-2">Statusi</th>
                <th className="text-right px-3 py-2">Fatura</th>
                <th className="text-right px-3 py-2">Shitur</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {workers.map(w => (
                <tr key={w.id} className="border-b border-[#1F2B44]/50" data-testid={`worker-row-${w.id}`}>
                  <td className="px-3 py-3 font-medium">{w.first_name} {w.last_name}</td>
                  <td className="px-3 py-3 text-xs text-neutral-400">{w.position || "—"}</td>
                  <td className="px-3 py-3 font-mono font-bold text-[#FFD600]">{w.pos_code}</td>
                  <td className="px-3 py-3 text-xs">{w.phone || "—"}</td>
                  <td className="px-3 py-3">
                    <button onClick={() => toggleActive(w)} data-testid={`worker-toggle-${w.id}`}
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 ${w.active !== false ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                      {w.active !== false ? "Aktiv" : "Deaktiv"}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-right">{w.invoices_count || 0}</td>
                  <td className="px-3 py-3 text-right font-bold">{fmtEUR(w.total_sold || 0)}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setEdit({ ...w })} data-testid={`worker-edit-${w.id}`} className="p-1.5 hover:bg-[#262626]"><Edit3 size={14} /></button>
                    <button onClick={() => del(w.id)} data-testid={`worker-del-${w.id}`} className="p-1.5 hover:bg-[#262626] text-red-400"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEdit(null)}>
          <div className="hg-card max-w-3xl w-full my-8 p-5" onClick={e => e.stopPropagation()}>
            <div className="font-display text-2xl uppercase mb-4">{edit.id ? "Ndrysho punëtorin" : "Shto punëtor"}</div>

            <div className="text-xs uppercase text-[#FFD600] font-bold mb-2">Të dhënat personale</div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Emri *</label>
                <input value={edit.first_name} onChange={e => setEdit({ ...edit, first_name: e.target.value })} data-testid="worker-first-name"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1 focus:border-[#FFD600] outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Mbiemri *</label>
                <input value={edit.last_name} onChange={e => setEdit({ ...edit, last_name: e.target.value })} data-testid="worker-last-name"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1 focus:border-[#FFD600] outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Telefon</label>
                <input value={edit.phone || ""} onChange={e => setEdit({ ...edit, phone: e.target.value })} data-testid="worker-phone"
                  placeholder="+383 44 ..." className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">E-posta</label>
                <input value={edit.email || ""} onChange={e => setEdit({ ...edit, email: e.target.value })} data-testid="worker-email"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-neutral-400">Adresa</label>
                <input value={edit.address || ""} onChange={e => setEdit({ ...edit, address: e.target.value })} data-testid="worker-address"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Nr. personal</label>
                <input value={edit.personal_id || ""} onChange={e => setEdit({ ...edit, personal_id: e.target.value })}
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Banka</label>
                <input value={edit.bank_name || ""} onChange={e => setEdit({ ...edit, bank_name: e.target.value })} data-testid="worker-bank"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-neutral-400">IBAN / Nr. i llogarisë</label>
                <input value={edit.iban || ""} onChange={e => setEdit({ ...edit, iban: e.target.value })} data-testid="worker-iban"
                  placeholder="XK05 ..." className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1 font-mono" />
              </div>
            </div>

            <div className="text-xs uppercase text-[#FFD600] font-bold mb-2">Kontrata e punës</div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Pozicioni</label>
                <input value={edit.position || ""} onChange={e => setEdit({ ...edit, position: e.target.value })} data-testid="worker-position"
                  placeholder="p.sh. Shitës, Menaxher..." className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Lloji i kontratës</label>
                <select value={edit.contract_type || "permanent"} onChange={e => setEdit({ ...edit, contract_type: e.target.value })} data-testid="worker-ctype"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1">
                  {CONTRACT_TYPES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Data e fillimit</label>
                <input type="date" value={edit.hire_date || ""} onChange={e => setEdit({ ...edit, hire_date: e.target.value })} data-testid="worker-hire"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Data e skadimit (nëse ka)</label>
                <input type="date" value={edit.contract_end_date || ""} onChange={e => setEdit({ ...edit, contract_end_date: e.target.value })}
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Orë / ditë</label>
                <input type="number" step="0.5" value={edit.hours_per_day ?? 8} onChange={e => setEdit({ ...edit, hours_per_day: parseFloat(e.target.value) || 0 })} data-testid="worker-hpd"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Orë / javë</label>
                <input type="number" step="0.5" value={edit.hours_per_week ?? 40} onChange={e => setEdit({ ...edit, hours_per_week: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Ditë pushimi / vit</label>
                <input type="number" value={edit.vacation_days_total ?? 21} onChange={e => setEdit({ ...edit, vacation_days_total: parseFloat(e.target.value) || 0 })} data-testid="worker-vac-days"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Paga mujore bazë (€)</label>
                <input type="number" step="0.01" value={edit.monthly_salary ?? 0} onChange={e => setEdit({ ...edit, monthly_salary: parseFloat(e.target.value) || 0 })} data-testid="worker-msalary"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Tarifa për orë (€)</label>
                <input type="number" step="0.01" value={edit.hourly_rate ?? 0} onChange={e => setEdit({ ...edit, hourly_rate: parseFloat(e.target.value) || 0 })} data-testid="worker-hrate"
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Tarifa për orë shtesë (€)</label>
                <input type="number" step="0.01" value={edit.overtime_rate ?? 0} onChange={e => setEdit({ ...edit, overtime_rate: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-neutral-400">Kodi hyrës (6 shifra) *</label>
                <div className="flex gap-2 mt-1">
                  <input value={edit.pos_code} onChange={e => setEdit({ ...edit, pos_code: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                    data-testid="worker-pos-code" maxLength={6}
                    className="flex-1 bg-[#131E30] border border-[#1F2B44] px-3 py-2 font-mono text-lg tracking-widest text-center focus:border-[#FFD600] outline-none" />
                  <button type="button" onClick={genCode} data-testid="worker-gen-code" className="px-4 border border-[#1F2B44] hover:border-[#FFD600] hover:text-[#FFD600] text-xs uppercase tracking-wider">Gjenero</button>
                </div>
                <div className="text-[10px] text-neutral-500 mt-1">Hyn te /arka/login me këtë kod.</div>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-neutral-400">Shënime shtesë (kontrata, etj.)</label>
                <textarea value={edit.notes || ""} onChange={e => setEdit({ ...edit, notes: e.target.value })} rows={2}
                  className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 mt-1 text-sm" />
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={() => setEdit(null)} className="flex-1 py-2.5 border border-[#1F2B44] uppercase text-xs">Anulo</button>
              <button onClick={save} data-testid="worker-save" className="flex-1 py-2.5 hg-btn-primary uppercase text-xs">Ruaj</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PosReportAdmin = () => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/pos/report?date=${date}`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [date]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <BarChart3 size={26} className="text-[#FFD600]" />
        <h1 className="font-display text-3xl uppercase">Raport POS</h1>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="admin-pos-date"
          className="ml-auto bg-[#131E30] border border-[#1F2B44] px-3 py-2 text-sm focus:border-[#FFD600] outline-none" />
      </div>

      {loading ? <div className="text-center py-10 text-neutral-400">Duke ngarkuar...</div> : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="hg-card p-4"><div className="text-xs uppercase text-neutral-400 mb-1">Totali</div><div className="text-2xl font-bold text-[#FFD600]">{fmtEUR(data.total)}</div></div>
            <div className="hg-card p-4"><div className="text-xs uppercase text-neutral-400 mb-1">Kesh</div><div className="text-2xl font-bold text-emerald-400">{fmtEUR(data.cash)}</div></div>
            <div className="hg-card p-4"><div className="text-xs uppercase text-neutral-400 mb-1">Kartë</div><div className="text-2xl font-bold text-blue-400">{fmtEUR(data.card)}</div></div>
            <div className="hg-card p-4"><div className="text-xs uppercase text-neutral-400 mb-1">Fatura</div><div className="text-2xl font-bold text-[#FFD600]">{fmtEUR(data.invoice)}</div></div>
          </div>
          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <div className="hg-card p-4"><div className="text-xs uppercase text-neutral-400 mb-1">Numri i shitjeve</div><div className="text-3xl font-bold">{data.count}</div></div>
            <div className="hg-card p-4"><div className="text-xs uppercase text-neutral-400 mb-1">TVSH</div><div className="text-3xl font-bold">{fmtEUR(data.vat)}</div></div>
          </div>

          <div className="hg-card p-4 mb-4">
            <div className="font-bold text-lg mb-3">Shitjet sipas punëtorit</div>
            {data.workers.length === 0 ? <div className="text-center py-4 text-neutral-500 text-sm">Ende s'ka shitje</div> : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-neutral-400 border-b border-[#1F2B44]">
                  <tr><th className="text-left py-2">Punëtori</th><th className="text-right py-2 pr-2">Fatura</th><th className="text-right py-2">Total</th></tr>
                </thead>
                <tbody>
                  {data.workers.map(w => (
                    <tr key={w.worker_id} className="border-b border-[#1F2B44]/50">
                      <td className="py-2">{w.worker_name}</td>
                      <td className="py-2 pr-2 text-right">{w.count}</td>
                      <td className="py-2 text-right font-bold">{fmtEUR(w.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="hg-card p-4">
            <div className="font-bold text-lg mb-3">Të gjitha faturat</div>
            {data.invoices.length === 0 ? <div className="text-center py-4 text-neutral-500 text-sm">S'ka fatura</div> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-neutral-400 border-b border-[#1F2B44]">
                    <tr><th className="text-left px-2 py-2">Nr</th><th className="text-left px-2 py-2">Ora</th><th className="text-left px-2 py-2">Punëtori</th><th className="text-left px-2 py-2">Pagesa</th><th className="text-right px-2 py-2">Totali</th><th className="text-left px-2 py-2">Statusi</th></tr>
                  </thead>
                  <tbody>
                    {data.invoices.map(inv => (
                      <tr key={inv.id} className="border-b border-[#1F2B44]/50">
                        <td className="px-2 py-2 font-mono text-xs">{inv.invoice_number}</td>
                        <td className="px-2 py-2 text-xs">{new Date(inv.created_at).toLocaleTimeString("sq")}</td>
                        <td className="px-2 py-2 text-xs">{inv.worker_name}</td>
                        <td className="px-2 py-2">
                          <span className={`text-[10px] uppercase px-2 py-0.5 ${
                            inv.payment_method === "cash" ? "bg-emerald-500/15 text-emerald-400"
                            : inv.payment_method === "card" ? "bg-blue-500/15 text-blue-400"
                            : "bg-[#FFD600]/15 text-[#FFD600]"
                          }`}>{inv.payment_method === "cash" ? "Kesh" : inv.payment_method === "card" ? "Kartë" : "Faturë"}</span>
                        </td>
                        <td className="px-2 py-2 text-right font-bold">{fmtEUR(inv.total)}</td>
                        <td className="px-2 py-2 text-[10px] uppercase">
                          {inv.status === "returned" ? <span className="text-red-400">E kthyer</span>
                            : inv.status === "partial_return" ? <span className="text-orange-400">Pjes.</span>
                            : <span className="text-emerald-400">OK</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
